import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"
import * as Mixpanel from "../lib/mixpanel"

// Crew Schedule (Larry, Kloepfer 2026-08-26; Phelipe 2026-09-10/11): the
// builder's global crew plan. Two tabs over the same blocks — Jobs (one
// block per job, one row per trade, a square per day: click or drag to fill,
// like his spreadsheet) and Crews (one row per trade across the picked jobs,
// red where a crew is needed on more jobs than the company has crews).
// Everything is drawn here from the data endpoint; every edit talks JSON and
// redraws in place. Nothing reloads the page.
const DAY_MS = 86400000
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
// A day is a DAY_W square on the Jobs tab; the board scrolls sideways past the pinned names.
const DAY_W = 28, TOP = 2, ROW = DAY_W, LABEL_W = 260, LABEL_W_MIN = 140, LABEL_W_MAX = 520 // LABEL_W is the default; the live width is this.state.labelW
const HOLIDAY_TINT = "rgba(147, 51, 234, 0.12)"
// Calendar days, not 24-hour steps (K1-30, Larry 2026-09-22): the day clocks go
// back is 25 hours long, so `date + n * DAY_MS` lands an hour early from then
// on and Nov 1, 2026 showed twice (spring-forward had "today" a day off).
// Local midnight plus n calendar days is right on both clock-change Sundays.
const addDays = (date, n) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + n)

// US federal holidays for a year, on the day they are observed.
function usHolidays(year) {
  const d = (m, day) => new Date(year, m - 1, day)
  const observed = (date) => { const w = date.getDay(); return w === 6 ? addDays(date, -1) : (w === 0 ? addDays(date, 1) : date) }
  const nth = (m, wday, n) => { const first = d(m, 1); return addDays(first, ((wday - first.getDay()) + 7) % 7 + (n - 1) * 7) }
  const last = (m, wday) => { const end = d(m + 1, 0); return addDays(end, -(((end.getDay() - wday) + 7) % 7)) }
  return [
    [observed(d(1, 1)), "New Year's Day"], [nth(1, 1, 3), "Martin Luther King Jr. Day"], [nth(2, 1, 3), "Presidents' Day"],
    [last(5, 1), "Memorial Day"], [observed(d(6, 19)), "Juneteenth"], [observed(d(7, 4)), "Independence Day"],
    [nth(9, 1, 1), "Labor Day"], [nth(10, 1, 2), "Columbus Day"], [observed(d(11, 11)), "Veterans Day"],
    [nth(11, 4, 4), "Thanksgiving"], [observed(d(12, 25)), "Christmas Day"]
  ]
}

export default class extends Controller {
  static targets = [
    "board", "conflicts", "legend", "tabCrews", "tabJobs", "crewsBadge", "jobsBadge",
    "jobsMenu", "jobsLabel", "jobsList", "tradesMenu", "tradesLabel", "tradesList",
    "from", "to", "lookahead", "onlyBox", "onlyWrap", "skipOffBox", "exportPdf", "tip", "popNote", "popNoteDay", "pickNote", "pickNoteDay", "pickNoteWrap", "pickAreaWrap", "pickArea", "rowsWrap", "rowsTrade", "rowsArea", "addAreaModal", "addAreaTitle", "addAreaInput", "areaMenu", "areaMenuTitle", "areaMenuMeta", "areaMenuName", "areaMenuHint", "areaMenuRemove", "linkJobModal", "linkJobTitle", "linkJobSelect", "linkJobCurrent", "linkJobUnmatch", "popMeta", "popEditLink", "editTradeModal", "editTradeForm", "editTradeMeta",
    "pop", "popTitle", "popDates", "popCrews", "popHave", "popPeople", "popPeopleHere", "lookaheadModal", "lookaheadForm", "pick", "pickTitle", "pickList",
    "rowMenu", "rowMenuTitle", "rowMenuMeta", "rowMenuTrades", "rowMenuRemove", "deleteTitle",
    "addTradeModal", "addTradeTitle", "addTradeList", "deleteModal", "deleteText", "manageModal", "manageList",
    "importModal", "importStep1", "importStep2", "importDrop", "importFile", "importFileName", "importYear", "importStatus", "importStatusText",
    "importError", "importWarnings", "importTrades", "importJobs", "importBack", "importApplyBtn", "importMode", "importModeWarn",
    "addJobModal", "addJobInput", "compareWrap", "compareBox", "compareRefresh", "filtersMenu", "filtersBadge",
    "renameJobModal", "renameJobInput", "renameJobHint", "linkJobUseName", "parked", "parkedWrap", "viewerCard",
    "historyModal", "historyList", "viewers"
  ]

  static values = {
    dataUrl: String, blocksUrl: String, paintUrl: String, tradesUrl: String, rowsUrl: String, pdfUrl: String, csrf: String,
    importPreviewUrl: String, importApplyUrl: String, notesUrl: String, shareUrl: String, lookaheadUrl: String, areasUrl: String, linkJobUrl: String, unlinkJobUrl: String, addJobUrl: String, refreshCompareUrl: String, reorderJobsUrl: String, historyUrl: String, renameJobUrl: String,
    companyId: Number, userId: Number,
    filters: Object, jobs: Array, trades: Array
  }

  connect() {
    const f = this.filtersValue || {}
    this.state = {
      tab: f.tab === "crews" ? "crews" : "jobs",
      jobIds: (f.job_ids || []).map(Number),
      tradeIds: f.trade_ids ? f.trade_ids.map(Number) : null,
      from: f.from, to: f.to,
      only: !!f.only_conflicts,
      skipOff: f.skip_off === undefined || f.skip_off === null ? true : !!f.skip_off,
      rowsBy: f.rows_by === "trade" ? "trade" : "area",
      collapsed: new Set((f.collapsed_job_ids || []).map(Number)),
      compare: !!f.compare,
      // The title column's width, as this person last dragged it.
      labelW: Number(f.label_w) || LABEL_W,
      highlight: null, showConflicts: false
    }
    this.trades = this.tradesValue || []
    this.jobs = []
    this.blocks = []
    this.notes = []
    this.areas = []
    this._paint = null
    this._move = null
    this._justPainted = false
    this._menuBlock = null
    this._pickAt = null
    this._onMove = (e) => this._paintMove(e)
    this._onUp = () => this._paintUp()
    this._onDocClick = (e) => { if (!e.target.closest("[data-menu]") && !e.target.closest("[data-menu-button]")) this.closeMenus() }
    this._onKey = (e) => { if (e.key === "Escape") { this.closeMenus(); this.closePop(); this.closePick(); this.closeRowMenu(); this.closeAddTrade(); this.hideDelete(); this.closeManage(); this.closeImport(); this.closeEditTrade(); this.closeLookahead(); this.closeAddArea(); this.closeAreaMenu(); this.closeLinkJob(); this.closeRenameJob() } }
    document.addEventListener("mousemove", this._onMove)
    document.addEventListener("mouseup", this._onUp)
    document.addEventListener("click", this._onDocClick)
    document.addEventListener("keydown", this._onKey)
    if (this.hasOnlyBoxTarget) this.onlyBoxTarget.checked = this.state.only
    if (this.hasSkipOffBoxTarget) this.skipOffBoxTarget.checked = this.state.skipOff
    if (this.hasCompareBoxTarget) this.compareBoxTarget.checked = this.state.compare
    this.load()
    this._subscribeLive()
  }

  disconnect() {
    document.removeEventListener("mousemove", this._onMove)
    document.removeEventListener("mouseup", this._onUp)
    document.removeEventListener("click", this._onDocClick)
    document.removeEventListener("keydown", this._onKey)
    this._unsubscribeLive()
  }

  // ── Data ──────────────────────────────────────────────────────────

  // ── Live: someone else changed the board (Phelipe 2026-09-17) ──
  // Their change arrives on the company's stream; we reload quietly and say
  // who did it. Our own changes are ignored — we already drew them.
  _subscribeLive() {
    if (!this.hasCompanyIdValue || !this.companyIdValue) return
    try {
      this._cable = createConsumer()
      this._liveSub = this._cable.subscriptions.create(
        { channel: "CrewScheduleChannel", company_id: this.companyIdValue },
        {
          received: (data) => {
            if (!data) return
            // Who is looking at this board right now.
            if (data.kind === "viewers") return this._renderViewers(data.viewers || [])
            if (Number(data.by) === Number(this.userIdValue)) return
            clearTimeout(this._liveTimer)
            this._livePending = data
            // Several edits in a row land as one refresh.
            this._liveTimer = setTimeout(() => {
              this.load()
              const who = this._livePending?.who || this._t("someone")
              this._toast(`${who}: ${this._livePending?.message || this._t("board-changed")}`)
            }, 600)
          }
        }
      )
      this._startHeartbeat()
    } catch (e) {
      // No cable, no live updates — the board still works exactly as before.
    }
  }

  // Say we are still here every half minute; the server drops anyone who
  // stops saying it, so a shut laptop disappears on its own.
  _startHeartbeat() {
    clearInterval(this._heartbeat)
    this._heartbeat = setInterval(() => {
      try { this._liveSub?.perform("here") } catch (_) { /* reconnecting */ }
    }, 30000)
  }

  // Faces, like a shared spreadsheet. Ours is left out — we know we are here.
  _renderViewers(list) {
    if (!this.hasViewersTarget) return
    const others = list.filter((v) => Number(v.id) !== Number(this.userIdValue))
    this.viewersTarget.classList.toggle("hidden", others.length === 0)
    this.viewersTarget.classList.toggle("flex", others.length > 0)
    this.viewersTarget.innerHTML = others.slice(0, 6).map((v) => {
      const name = this._esc(v.name || "")
      const ring = "w-7 h-7 rounded-full ring-2 ring-white -ml-1.5 first:ml-0 shrink-0"
      const hov = `data-viewer-id="${v.id}" data-viewer-name="${name}" data-action="mouseenter->crew-schedule#viewerEnter mouseleave->crew-schedule#viewerLeave"`
      return v.avatar
        ? `<img src="${this._esc(v.avatar)}" alt="${name}" ${hov} class="${ring} object-cover cursor-default">`
        : `<span ${hov} class="${ring} bg-brand/15 text-brand text-[11px] font-semibold flex items-center justify-center cursor-default">${this._esc((v.initials || name).toString().slice(0, 2).toUpperCase())}</span>`
    }).join("") + (others.length > 6 ? `<span class="ml-1 text-[12px] text-gray-500">+${others.length - 6}</span>` : "")
  }

  // Hovering someone's face says who they are and everything they have changed
  // today (Phelipe 2026-09-17). One fetch, cached, shared by every face.
  async viewerEnter(e) {
    if (!this.hasViewerCardTarget) return
    const el = e.currentTarget, id = Number(el.dataset.viewerId), name = el.dataset.viewerName || ""
    this._viewerOver = id
    const card = this.viewerCardTarget, r = el.getBoundingClientRect()
    card.style.top = `${Math.round(r.bottom + 8)}px`
    card.style.left = `${Math.round(Math.max(8, Math.min(r.left - 100, window.innerWidth - 296)))}px`
    card.innerHTML = this._viewerCardHtml(name, null)
    card.classList.remove("hidden")
    const rows = await this._todayActions()
    if (this._viewerOver !== id) return
    card.innerHTML = this._viewerCardHtml(name, rows.filter((x) => Number(x.user_id) === id))
  }

  viewerLeave() {
    this._viewerOver = null
    if (this.hasViewerCardTarget) this.viewerCardTarget.classList.add("hidden")
  }

  _viewerCardHtml(name, rows) {
    const head = `<div class="px-3 py-2 border-b border-gray-200 bg-gray-50"><div class="font-semibold text-gray-800 truncate">${this._esc(name)}</div><div class="text-[12px] text-gray-500">${this._esc(this._t("viewer-today"))}</div></div>`
    if (rows === null) return head + `<div class="px-3 py-3 text-gray-400">${this._esc(this._t("loading"))}</div>`
    if (!rows.length) return head + `<div class="px-3 py-3 text-gray-400">${this._esc(this._t("viewer-none"))}</div>`
    return head + `<ul class="max-h-64 overflow-y-auto divide-y divide-gray-100">` + rows.map((x) => {
      const at = x.at_time || ""
      return `<li class="px-3 py-2 flex items-baseline gap-2"><span class="text-gray-400 text-[12px] whitespace-nowrap shrink-0">${this._esc(at)}</span><span class="text-gray-700 flex-1 min-w-0">${this._esc(x.message || x.action)}</span></li>`
    }).join("") + `</ul>`
  }

  async _todayActions() {
    if (this._todayCache && Date.now() - this._todayCache.at < 20000) return this._todayCache.rows
    try {
      const res = await fetch(`${this.historyUrlValue}?today=1`, { headers: { Accept: "application/json" } })
      const rows = (await res.json()).entries || []
      this._todayCache = { at: Date.now(), rows }
      return rows
    } catch { return [] }
  }

  _unsubscribeLive() {
    clearInterval(this._heartbeat)
    clearTimeout(this._liveTimer)
    try { this._liveSub?.unsubscribe() } catch (_) { /* already gone */ }
    try { this._cable?.disconnect() } catch (_) { /* already gone */ }
  }

  // ── Who changed what, and when (Phelipe 2026-09-17) ──
  async openHistory() {
    if (!this.hasHistoryModalTarget) return
    this.historyModalTarget.classList.remove("hidden")
    this.historyListTarget.innerHTML = `<p class="text-gray-400 py-6 text-center">${this._esc(this._t("loading"))}</p>`
    try {
      const res = await fetch(this.historyUrlValue, { headers: { Accept: "application/json" } })
      const data = await res.json()
      const rows = data.entries || []
      if (!rows.length) {
        this.historyListTarget.innerHTML = `<p class="text-gray-400 py-6 text-center">${this._esc(this._t("history-empty"))}</p>`
        return
      }
      this.historyListTarget.innerHTML = `<ul class="divide-y divide-gray-100">${rows.map((r) => this._historyRow(r)).join("")}</ul>`
    } catch {
      this.historyListTarget.innerHTML = `<p class="text-red-600 py-6 text-center">${this._esc(this._t("error"))}</p>`
    }
  }

  closeHistory() { if (this.hasHistoryModalTarget) this.historyModalTarget.classList.add("hidden") }

  _historyRow(r) {
    // The exact detail sits under the sentence, so "what changed" is answerable.
    const bits = Object.entries(r.detail || {})
      .filter(([, v]) => v !== null && v !== "" && v !== undefined)
      .map(([key, v]) => `<span class="inline-flex items-center gap-1 mr-2 whitespace-nowrap"><span class="text-gray-400">${this._esc(key.replace(/_/g, " "))}</span><span class="text-gray-700">${this._esc(Array.isArray(v) ? v.join(" → ") : String(v))}</span></span>`)
      .join("")
    return `<li class="py-2.5">
      <div class="flex items-baseline gap-2">
        <span class="font-medium text-gray-800">${this._esc(r.who)}</span>
        <span class="text-gray-700 flex-1 min-w-0">${this._esc(r.message || r.action)}</span>
        <span class="text-gray-400 whitespace-nowrap">${this._esc(r.at_label || "")}</span>
      </div>
      ${bits ? `<div class="mt-1 text-[12px]">${bits}</div>` : ""}
    </li>`
  }

  // ── Drag the title column wider or narrower (Phelipe 2026-09-17) ──
  // Live: only the CSS variable moves while dragging, so the board does not
  // redraw on every pixel. The width is saved when the mouse comes up.
  labelResizeDown(e) {
    const grip = e.target.closest("[data-label-resize]")
    if (!grip) return
    e.preventDefault(); e.stopPropagation()
    const startX = e.clientX, startW = this.state.labelW
    const move = (ev) => {
      const w = Math.min(LABEL_W_MAX, Math.max(LABEL_W_MIN, startW + (ev.clientX - startX)))
      this.state.labelW = w
      this.boardTarget.style.setProperty("--cs-label-w", `${w}px`)
    }
    const up = () => {
      document.removeEventListener("mousemove", move)
      document.removeEventListener("mouseup", up)
      document.body.style.cursor = ""
      this.render()          // the pixel maths (sticky widths, drop slots) catches up
      this._persist()        // and the width is remembered for next time
      this._track("Crew Title Column Resized", { width: this.state.labelW })
    }
    document.body.style.cursor = "col-resize"
    document.addEventListener("mousemove", move)
    document.addEventListener("mouseup", up)
  }

  // Double-click the grip to put it back to the standard width.
  labelResizeReset(e) {
    if (!e.target.closest("[data-label-resize]")) return
    e.preventDefault(); e.stopPropagation()
    this.state.labelW = LABEL_W
    this.render(); this._persist()
  }

  _query() {
    const s = this.state, q = new URLSearchParams()
    s.jobIds.forEach((id) => q.append("job_ids[]", id)); if (!s.jobIds.length) q.append("job_ids[]", "")
    // null = every trade. It has to be SAID, or the server keeps the last
    // saved subset and "All" is forgotten on refresh (Phelipe 2026-09-16).
    if (s.tradeIds) { s.tradeIds.forEach((id) => q.append("trade_ids[]", id)); if (!s.tradeIds.length) q.append("trade_ids[]", "") }
    else q.append("trade_ids_all", "1")
    q.append("from", s.from); q.append("to", s.to); q.append("tab", s.tab); q.append("only_conflicts", s.only ? "1" : "0"); q.append("skip_off", s.skipOff ? "1" : "0"); q.append("rows_by", s.rowsBy)
    s.collapsed.forEach((id) => q.append("collapsed_job_ids[]", id)); if (!s.collapsed.size) q.append("collapsed_job_ids[]", "")
    q.append("compare", s.compare ? "1" : "0")
    q.append("label_w", s.labelW)
    return q
  }

  async load() {
    try {
      const res = await fetch(`${this.dataUrlValue}?${this._query()}`, { headers: { Accept: "application/json" } })
      const data = await res.json()
      if (!res.ok) { this._toast(data.error || this._t("error"), "error"); return }
      this.state.from = data.from; this.state.to = data.to
      this.trades = data.trades; this.jobs = data.jobs; this.blocks = data.blocks; this.notes = data.notes || []; this.areas = data.areas || []
      this.comparison = data.comparison || null
      this.render()
      this._pollComparison()
    } catch { this._toast(this._t("error"), "error") }
  }

  async _persist() {
    try { await fetch(`${this.dataUrlValue}?${this._query()}`, { headers: { Accept: "application/json" } }) } catch { /* the picks just don't stick this time */ }
  }

  // ── Tabs, pickers, range ──────────────────────────────────────────

  showCrews() { this.state.tab = "crews"; this.closePop(); this.closePick(); this.render(); this._persist() }
  showJobs() { this.state.tab = "jobs"; this.state.highlight = null; this.state.only = false; if (this.hasOnlyBoxTarget) this.onlyBoxTarget.checked = false; this.render(); this._persist() }
  toggleOnly() { this.state.only = this.onlyBoxTarget.checked; if (this.state.only) this.state.tab = "crews"; this.render(); this._persist() }
  toggleSkipOff() { this.state.skipOff = this.skipOffBoxTarget.checked; this._persist() }

  // Weekends and US holidays are off days: dragging skips them, a moved block hops over them.
  _isOff(day) { const dow = this._dateAt(day).getDay(); return dow === 0 || dow === 6 || !!this.holidays[day] }
  _workDays(from, count) { const out = []; for (let d = from; d < this.range && out.length < count; d++) if (!this._isOff(d)) out.push(d); return out }
  rangeChanged() {
    const from = this.fromTarget.value, to = this.toTarget.value
    if (!from || !to) return
    if (this._parse(to) < this._parse(from)) { this._toast(this._t("error"), "error"); return }
    this.state.from = from; this.state.to = to
    this.load()
  }

  // "Look ahead": from this week's Monday, N days out.
  lookAhead() {
    const n = Number(this.lookaheadTarget.value)
    if (!n) return
    const now = new Date(); const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const monday = addDays(today, -((today.getDay() + 6) % 7))
    const from = this._iso(monday.getTime()), to = this._iso(addDays(monday, n - 1).getTime())
    this._setDate(this.fromTarget, from); this._setDate(this.toTarget, to)
    this.state.from = from; this.state.to = to
    this.load()
  }

  _setDate(input, iso) {
    input.value = iso
    if (input._flatpickr) input._flatpickr.setDate(iso, false)
  }

  _syncLookahead() {
    if (!this.hasLookaheadTarget) return
    const now = new Date(); const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const monday = this._iso(addDays(today, -((today.getDay() + 6) % 7)).getTime())
    const n = this.range
    const preset = [7, 14, 30, 60, 90, 182, 365].includes(n) && this.state.from === monday ? String(n) : ""
    this.lookaheadTarget.value = preset
  }

  toggleJobsMenu(e) { e.stopPropagation(); const open = this.jobsMenuTarget.classList.contains("hidden"); this.closeMenus(); if (open) this.jobsMenuTarget.classList.remove("hidden") }
  toggleTradesMenu(e) { e.stopPropagation(); const open = this.tradesMenuTarget.classList.contains("hidden"); this.closeMenus(); if (open) this.tradesMenuTarget.classList.remove("hidden") }
  toggleFiltersMenu(e) {
    e.stopPropagation()
    const m = this.filtersMenuTarget, open = m.classList.contains("hidden")
    this.closeMenus()
    if (!open) return
    // The pill sits near the right edge on wide screens: open the panel leftwards when it would run off the page.
    m.classList.remove("hidden"); m.classList.add("left-0"); m.classList.remove("right-0")
    if (m.getBoundingClientRect().right > window.innerWidth - 8) { m.classList.remove("left-0"); m.classList.add("right-0") }
  }
  closeMenus() { if (this.hasJobsMenuTarget) this.jobsMenuTarget.classList.add("hidden"); if (this.hasTradesMenuTarget) this.tradesMenuTarget.classList.add("hidden"); if (this.hasFiltersMenuTarget) this.filtersMenuTarget.classList.add("hidden") }
  allJobs() { this.state.jobIds = this.jobsValue.map((p) => p.id); this.load() }
  noJobs() { this.state.jobIds = []; this.load() }
  allTrades() { this.state.tradeIds = null; this.load() }
  noTrades() { this.state.tradeIds = []; this.load() }
  toggleJob(e) {
    const id = Number(e.currentTarget.dataset.jobId)
    const set = new Set(this.state.jobIds)
    set.has(id) ? set.delete(id) : set.add(id)
    this.state.jobIds = Array.from(set)
    this.load()
  }
  restoreJob(e) {
    const id = Number(e.currentTarget.dataset.jobId)
    if (this.state.jobIds.includes(id)) return
    this.state.collapsed.add(id)
    this.state.jobIds = this.state.jobIds.concat([id])
    this.load()
    this._track("Crew Job Restored", { job_id: id })
  }
  toggleTrade(e) {
    const id = Number(e.currentTarget.dataset.tradeId)
    const set = new Set(this.state.tradeIds || this.trades.map((t) => t.id))
    set.has(id) ? set.delete(id) : set.add(id)
    this.state.tradeIds = Array.from(set)
    if (this.state.tradeIds.length === this.trades.length) this.state.tradeIds = null
    this.load()
  }

  // ── Render ────────────────────────────────────────────────────────

  render() {
    const s = this.state
    const from = this._parse(s.from), to = this._parse(s.to)
    this.range = Math.max(1, Math.round((to - from) / DAY_MS) + 1)
    this.fromDate = from
    const pct = (d) => Math.max(0, Math.min(this.range, d)) * DAY_W // pixels along the track
    this.pct = pct
    this.trackW = this.range * DAY_W
    this.holidays = {}
    for (let y = from.getFullYear(); y <= to.getFullYear(); y++) usHolidays(y).forEach(([date, name]) => { const i = Math.round((date - from) / DAY_MS); if (i >= 0 && i < this.range) this.holidays[i] = name })
    this._noteMap = {}
    ;(this.notes || []).forEach((n) => { const d = this._day(n.on); if (d >= 0 && d < this.range) this._noteMap[`${n.job_id}:${n.trade_id}:${d}`] = n })
    this.hideTip()

    const tradeSet = new Set(s.tradeIds || this.trades.map((t) => t.id))
    const activeTrades = this.trades.filter((t) => tradeSet.has(t.id))
    const allTrades = activeTrades.length === this.trades.length
    const selected = this._orderedJobs(this.jobs)
    const byProject = {}; selected.forEach((p) => { byProject[p.id] = p })

    const mkBar = (b, project, trade, color, text, label, h) => {
      const sd = Math.max(0, this._day(b.start_on)), ed = Math.min(this.range, this._day(b.end_on) + 1)
      if (ed <= sd) return null
      const crews = Number(b.crews) || 1
      return { id: b.id, pid: project.id, name: project.name, tradeId: trade.id, crews, s: sd, e: ed, left: pct(sd), width: Math.max(4, pct(ed) - pct(sd)), color, text, label: (ed - sd) >= 3 ? `${label}${crews > 1 ? ` ×${crews}` : ""}`.trim() : "", h, top: 0,
        tip: `${project.name} · ${trade.name}${b.area ? " · " + b.area : ""} · ${this._fmt(sd)} – ${this._fmtEnd(ed)}`, done: ed <= this._todayIdx() }
    }
    const stack = (bars, top, row) => {
      bars.sort((a, b) => a.s - b.s || a.e - b.e)
      const ends = []
      bars.forEach((b) => { let r = ends.findIndex((end) => end <= b.s); if (r === -1) { r = ends.length; ends.push(b.e) } else ends[r] = b.e; b.top = top + r * row })
      return Math.max(1, ends.length)
    }
    const laneH = (rows, conf) => conf ? TOP + rows * ROW + 24 : Math.max(52, TOP + rows * ROW + 6)
    const center = (bars, rows, h) => { const off = Math.floor((h - (TOP + rows * ROW + 6)) / 2); if (off > 0) bars.forEach((b) => { b.top += off }) }
    const conflictsFor = (bars, trade) => {
      if (trade.sub) return []
      const ev = []; bars.forEach((b) => { ev.push({ t: b.s, d: 1, b }); ev.push({ t: b.e, d: -1, b }) })
      ev.sort((x, y) => x.t - y.t || x.d - y.d)
      const active = new Set(); const out = []; let cur = null
      ev.forEach((e) => {
        if (e.d === 1) active.add(e.b); else active.delete(e.b)
        const perJob = {}; active.forEach((b) => { perJob[b.pid] = Math.max(perJob[b.pid] || 0, b.crews || 1) })
        const needed = Object.values(perJob).reduce((n, v) => n + v, 0)
        if (needed > trade.crews) {
          if (!cur) cur = { s: e.t, e: null, max: 0, names: [], pids: [] }
          cur.max = Math.max(cur.max, needed)
          active.forEach((b) => { if (!cur.names.includes(b.name)) cur.names.push(b.name); if (!cur.pids.includes(b.pid)) cur.pids.push(b.pid) })
        } else if (cur) { cur.e = e.t; out.push(cur); cur = null }
      })
      return out.map((c) => ({ key: `c${trade.id}-${c.s}`, tradeId: trade.id, pids: c.pids, s: c.s, e: c.e, left: pct(c.s), width: Math.max(4, pct(c.e) - pct(c.s)), label: `${this._t("needs")} ${c.max} · ${this._t("has")} ${trade.crews}`, names: c.names.join(" + ") }))
    }

    // Crews tab lanes (always computed: the conflict count feeds the tab badge)
    this.crewLanes = activeTrades.map((t) => {
      const bars = []
      this.blocks.filter((b) => b.trade_id === t.id && byProject[b.job_id]).forEach((b) => { const bar = mkBar(b, byProject[b.job_id], t, byProject[b.job_id].color, "#ffffff", byProject[b.job_id].name, DAY_W - 4); if (bar) bars.push(bar) })
      const rows = stack(bars, TOP, ROW); const conflicts = conflictsFor(bars, t)
      const jobs = new Set(bars.map((b) => b.pid)).size
      const h = laneH(rows, conflicts.length > 0); center(bars, rows, h)
      const meta = (t.sub ? this._t("sub") + " · " : (t.crews > 1 ? `${t.crews} ${this._t("crews-word")} · ` : "")) + (jobs ? `${jobs} ${jobs === 1 ? this._t("job-word") : this._t("jobs-word")}` : this._t("free"))
      return { key: `t${t.id}`, trade: t, name: t.name, meta, height: h, bars, conflicts, muted: !jobs }
    })
    const conflictList = []
    this.crewLanes.forEach((l) => l.conflicts.forEach((c) => conflictList.push({ lane: l, c })))
    const n = conflictList.length
    this.conflictList = conflictList.map(({ c }) => c)
    if (n === 0) { s.showConflicts = false; s.highlight = null }

    // Chrome: subtitle, tabs, badges, labels, export link
    if (this.hasRowsWrapTarget) {
      this.rowsWrapTarget.classList.toggle("hidden", s.tab !== "jobs"); this.rowsWrapTarget.classList.toggle("flex", s.tab === "jobs")
      if (this.hasCompareWrapTarget) this.compareWrapTarget.classList.toggle("hidden", s.tab !== "jobs")
      if (this.hasCompareRefreshTarget) this.compareRefreshTarget.classList.toggle("hidden", !(s.tab === "jobs" && s.compare))
      this._styleSeg(this.rowsTradeTarget, s.rowsBy !== "area"); this._styleSeg(this.rowsAreaTarget, s.rowsBy === "area")
    }
    this._styleTab(this.tabCrewsTarget, s.tab === "crews"); this._styleTab(this.tabJobsTarget, s.tab === "jobs")
    if (this.hasOnlyWrapTarget) this.onlyWrapTarget.classList.toggle("hidden", s.tab !== "crews") // the conflicts-only switch is a Crews tab thing
    if (this.hasFiltersBadgeTarget) { // how many switches are on, for the Filters pill
      const on = (s.skipOff ? 1 : 0) + (s.tab === "jobs" && s.compare ? 1 : 0) + (s.tab === "crews" && s.only ? 1 : 0)
      this.filtersBadgeTarget.textContent = on; this.filtersBadgeTarget.classList.toggle("hidden", on === 0)
    }
    this.crewsBadgeTarget.textContent = n; this.crewsBadgeTarget.classList.toggle("hidden", n === 0 || selected.length === 0)
    this.jobsBadgeTarget.textContent = selected.length
    this.jobsLabelTarget.textContent = `${selected.length} of ${this.jobsValue.length} ${this._t("jobs-word")}`
    this.tradesLabelTarget.textContent = allTrades ? "All trades" : `${activeTrades.length} of ${this.trades.length} trades`
    this._renderPickers(tradeSet)
    this._syncLookahead()
    if (this.hasExportPdfTarget) this.exportPdfTarget.href = `${this.pdfUrlValue}?${this._query()}`

    // Conflict strip
    if (selected.length === 0) this.conflictsTarget.innerHTML = ""
    else if (n === 0) this.conflictsTarget.innerHTML = `<span class="inline-flex items-center gap-1.5 text-[13px] text-green-700">${this._svgCheck()}${this._esc(this._t("no-conflicts"))}</span>`
    else this.conflictsTarget.innerHTML = `<button type="button" data-action="click->crew-schedule#toggleConflicts" title="${this._esc(this._t("show-conflicts"))}" class="inline-flex items-center gap-1.5 h-6 px-2.5 rounded-full text-[13px] font-semibold cursor-pointer ${s.showConflicts ? "bg-red-600 text-white" : "bg-red-100 text-red-700 hover:bg-red-200"}">${n} ${n === 1 ? this._t("conflict") : this._t("conflicts")}<svg width="10" height="6" viewBox="0 0 10 6" fill="currentColor" style="transform:rotate(${s.showConflicts ? 180 : 0}deg)"><path d="M0 0l5 6 5-6z"/></svg></button>` +
      (s.showConflicts ? conflictList.map(({ lane, c }) => `<button type="button" data-chip-key="${c.key}" data-action="click->crew-schedule#chipClick" class="inline-flex items-center gap-1.5 h-[26px] px-2.5 rounded-lg border text-[13px] cursor-pointer whitespace-nowrap ${s.highlight === c.key ? "border-red-600 bg-red-100" : "border-red-200 bg-white hover:bg-red-50"} text-red-800"><span class="font-semibold">${this._esc(lane.name)} · ${this._fmt(c.s)} – ${this._fmtEnd(c.e)}</span><span class="text-gray-400">·</span><span>${this._esc(c.names)}</span></button>`).join("") : "")

    // Board
    let lanesHtml = ""
    if (selected.length === 0) {
      lanesHtml = `<div class="px-6 py-12 text-center text-[13px] text-gray-400">${this._esc(this._t("pick-job"))}</div>`
    } else if (s.tab === "crews") {
      const lanes = s.only ? this.crewLanes.filter((l) => l.conflicts.length) : this.crewLanes
      if (!activeTrades.length) lanesHtml = this._noteRow(this._t("pick-trade"))
      else if (!lanes.length) lanesHtml = this._noteRow(this._t("no-conflicts"))
      else lanesHtml = lanes.map((l) => this._crewLaneHtml(l)).join("")
    } else {
      this._lineCards = []
      lanesHtml = selected.map((p) => (s.rowsBy === "area" ? this._jobAreaHtml(p, activeTrades, mkBar, stack) : this._jobHtml(p, activeTrades, mkBar, stack))).join("")
    }
    const keepScroll = this.boardTarget.scrollLeft
    this.boardTarget.style.setProperty("--cs-label-w", `${this.state.labelW}px`)
    this.boardTarget.innerHTML = (selected.length ? this._headerHtml() : "") + lanesHtml
    this._placeScroll(keepScroll)

    // Jobs that were ticked off the board wait on a shelf underneath: just the
    // name and an empty box (Phelipe 2026-09-17). Ticking one calls the SAME
    // switch as the Jobs picker, so it goes straight back to its place above.
    if (this.hasParkedTarget) {
      const on = new Set(this.state.jobIds)
      const parked = (this.jobsValue || []).filter((p) => !on.has(p.id))
      if (this.hasParkedWrapTarget) this.parkedWrapTarget.classList.toggle("hidden", parked.length === 0)
      this.parkedTarget.innerHTML = parked.map((p) => `<button type="button" data-job-id="${p.id}" data-action="click->crew-schedule#restoreJob" title="${this._esc(this._t("park-restore"))}" class="w-full flex items-center gap-2 px-3 h-[30px] border-b border-gray-100 last:border-b-0 text-[13px] text-gray-500 hover:bg-gray-50 hover:text-gray-800 cursor-pointer text-left"><span class="inline-flex items-center justify-center w-3 h-3 rounded-[2px] bg-white border border-gray-300 shrink-0"></span><span class="truncate">${this._esc(p.name)}</span></button>`).join("")
    }

    // Legend
    const holidayNote = `<span class="inline-flex items-center gap-1.5 text-gray-500" title="${this._esc(Object.values(this.holidays).join(", "))}"><span class="w-3 h-3 rounded-[3px] border border-purple-200" style="background:${HOLIDAY_TINT}"></span>US holiday</span>`
    if (s.tab === "crews") this.legendTarget.innerHTML = selected.map((p) => `<span class="inline-flex items-center gap-1.5"><span class="w-3 h-3 rounded-[3px]" style="background:${p.color}"></span>${this._esc(p.name)}</span>`).join("") + holidayNote
    else this.legendTarget.innerHTML = activeTrades.map((t) => `<span class="inline-flex items-center gap-1.5" title="${t.sub ? this._esc(this._t("sub")) : ""}"><span class="w-3 h-3 rounded-[3px]" style="background:${t.color}"></span>${this._esc(t.name)}${t.crews > 1 ? `<span class="text-gray-400">×${t.crews}</span>` : ""}</span>`).join("") + holidayNote
  }

  // Where the board sits sideways after a redraw (K1-30, Larry 2026-09-22):
  // a new start date — picked in From or set by Look ahead — shows that date
  // at the left edge. Before, the old scroll position survived the redraw, so
  // moving From back a month still opened on the 19th. Any other redraw keeps
  // the spot; the very first one opens a few days before today.
  _placeScroll(keep) {
    const from = this.state.from
    const moved = this._shownFrom !== undefined && this._shownFrom !== from
    this._shownFrom = from
    if (moved) { this.boardTarget.scrollLeft = 0; return }
    if (this._scrolledOnce) { this.boardTarget.scrollLeft = keep; return }
    const t = this._todayIdx()
    this.boardTarget.scrollLeft = t === null ? 0 : Math.max(0, t * DAY_W - 3 * DAY_W)
    this._scrolledOnce = true
  }

  _renderPickers(tradeSet) {
    const box = (on) => this._box(on)
    const picked = new Set(this.state.jobIds)
    const planned = new Set(this.blocks.map((b) => b.job_id))
    this.jobsListTarget.innerHTML = this.jobsValue.map((p) => `<button type="button" data-job-id="${p.id}" data-action="click->crew-schedule#toggleJob" class="w-full flex items-center gap-2.5 px-3 py-1.5 text-[13px] text-gray-700 hover:bg-gray-50 cursor-pointer text-left">${box(picked.has(p.id))}<span class="flex-1 truncate">${this._esc(p.name)}</span>${picked.has(p.id) && !planned.has(p.id) ? '<span class="text-gray-400 italic">no crew plan yet</span>' : ""}${p.crew_only ? "" : `<span title="${this._esc(this._t("job-matched"))}" class="inline-flex items-center justify-center w-5 h-5 text-purple-600 shrink-0">${this._linkSvg()}</span>`}</button>`).join("")
    this.tradesListTarget.innerHTML = this.trades.map((t) => `<button type="button" data-trade-id="${t.id}" data-action="click->crew-schedule#toggleTrade" class="w-full flex items-center gap-2.5 px-3 py-1.5 text-[13px] text-gray-700 hover:bg-gray-50 cursor-pointer text-left">${box(tradeSet.has(t.id))}<span class="w-3 h-3 rounded-[3px] shrink-0" style="background:${t.color}"></span><span class="flex-1 truncate">${this._esc(t.name)}</span></button>`).join("")
  }

  _headerHtml() {
    // Like his spreadsheet: the month on top, then the day number and the weekday letter over every square.
    const months = []; const days = []
    const LETTERS = ["S", "M", "T", "W", "T", "F", "S"]
    for (let i = 0; i < this.range; i++) {
      const dd = this._dateAt(i)
      if (i === 0 || dd.getDate() === 1) months.push({ s: i, month: dd.getMonth(), year: dd.getFullYear() })
      days.push({ left: this.pct(i), num: dd.getDate(), letter: LETTERS[dd.getDay()], monday: dd.getDay() === 1, today: i === this._todayIdx(), holiday: this.holidays[i] || "" })
    }
    months.forEach((m, i) => { m.left = this.pct(m.s); m.width = this.pct(i + 1 < months.length ? months[i + 1].s : this.range) - m.left })
    const today = this._todayIdx()
    const monthHtml = months.map((m, i) => {
      const full = `${MONTHS[m.month]} ${m.year}`
      const label = m.width >= 96 ? full : (m.width >= 40 ? MONTHS[m.month] : "")
      // Centred over its month, stepping aside when the Today badge would sit on top of it.
      let center = m.left + m.width / 2
      if (today !== null && this.pct(today) >= m.left && this.pct(today) < m.left + m.width) {
        const tx = this.pct(today) + DAY_W / 2, lw = label.length * 7.5 + 8, bw = 52
        if (Math.abs(center - tx) < (lw + bw) / 2) {
          const leftRoom = (tx - bw / 2) - m.left, rightRoom = (m.left + m.width) - (tx + bw / 2)
          center = leftRoom >= rightRoom ? m.left + leftRoom / 2 : (tx + bw / 2) + rightRoom / 2
        }
      }
      return `<div data-month="${m.year}-${m.month + 1}" class="absolute top-0 box-border border-l border-b border-gray-300${i === months.length - 1 ? " border-r" : ""}" style="height:26px;left:${m.left}px;width:${m.width}px"></div>` +
        `<div data-month-label="1" class="absolute top-0 -translate-x-1/2 flex items-center text-[13px] font-medium text-gray-700 whitespace-nowrap pointer-events-none" style="height:26px;left:${center}px">${label}</div>`
    }).join("")
    return `<div class="flex border-b border-gray-200 bg-gray-50 sticky top-0 z-[3]">
      <div style="width:var(--cs-label-w)" class="relative shrink-0 sticky left-0 z-[2] bg-gray-50 border-r border-gray-200 flex flex-col justify-between px-3 pt-2 pb-1 text-[13px] font-medium text-gray-500">
        ${this.state.tab === "crews" ? "<span></span>" : `<button type="button" data-action="click->crew-schedule#toggleAll" class="self-start text-brand font-medium hover:underline cursor-pointer whitespace-nowrap">${this._esc(this._t(this._allFolded() ? "expand-all" : "collapse-all"))}</button>`}
        <span>${this.state.tab === "crews" ? "Trade" : (this.state.rowsBy === "area" ? "Job / area" : "Job / trade")}</span>
        <div data-label-resize title="${this._esc(this._t("resize-title"))}"
             class="group absolute top-0 right-0 h-full w-[9px] -mr-[4px] z-[4] cursor-col-resize flex justify-center">
          <span class="w-[2px] h-full bg-gray-300 group-hover:bg-brand transition-colors"></span>
        </div>
      </div>
      <div class="relative shrink-0" style="height:68px;width:${this.trackW}px">
        ${monthHtml}
        ${this._weekendHtml(26)}
        ${days.map((d) => `<div title="${this._esc(d.holiday)}" class="absolute box-border flex items-center justify-center text-[13px] ${d.today ? "font-semibold text-brand" : (d.holiday ? "font-semibold text-purple-700" : "text-gray-600")} ${d.monday ? "border-l border-gray-200" : ""}" style="top:26px;height:22px;left:${d.left}px;width:${DAY_W}px">${d.num}</div><div title="${this._esc(d.holiday)}" class="absolute box-border flex items-center justify-center text-[13px] ${d.today ? "font-semibold text-brand" : (d.holiday ? "text-purple-700" : "text-gray-400")} ${d.monday ? "border-l border-gray-200" : ""}" style="top:48px;height:20px;left:${d.left}px;width:${DAY_W}px">${d.letter}</div>`).join("")}
        ${today !== null ? `<div class="absolute -translate-x-1/2 h-5 px-1.5 rounded bg-brand text-white text-[13px] font-semibold leading-5 whitespace-nowrap" style="top:3px;left:${this.pct(today) + DAY_W / 2}px">Today</div>` : ""}
      </div></div>`
  }

  _weekendHtml(top = 0) {
    let html = ""
    for (let i = 0; i < this.range; i++) {
      const dow = this._dateAt(i).getDay()
      if (this.holidays[i]) html += `<div class="absolute bottom-0 pointer-events-none" style="top:${top}px;left:${this.pct(i)}px;width:${DAY_W}px;background:${HOLIDAY_TINT}"></div>`
      else if (dow === 6 || dow === 0) html += `<div class="absolute bottom-0 pointer-events-none" style="top:${top}px;left:${this.pct(i)}px;width:${DAY_W}px;background:rgba(128,128,128,0.10)"></div>`
    }
    return html
  }

  _trackExtras() {
    const today = this._todayIdx()
    let html = ""
    for (let i = 0; i < this.range; i++) {
      const dd = this._dateAt(i)
      if (dd.getDate() === 1 && i > 0) html += `<div class="absolute top-0 bottom-0 pointer-events-none" style="left:${this.pct(i)}px;width:1px;background:var(--color-gray-300, #d1d5db)"></div>`
      else if (dd.getDay() === 1) html += `<div class="absolute top-0 bottom-0 pointer-events-none" style="left:${this.pct(i)}px;width:1px;background:var(--color-gray-200, #e5e7eb)"></div>`
    }
    html += this._weekendHtml(0)
    if (today !== null) html += `<div class="absolute top-0 bottom-0 pointer-events-none" style="left:${this.pct(today) + DAY_W / 2}px;width:2px;background:#c2410c;opacity:.85"></div>`
    return html
  }

  // The day squares of a row: one per day, click or drag to fill.
  _cellsHtml(bars = []) {
    const filled = new Set()
    bars.forEach((b) => { for (let d = b.s; d < b.e; d++) filled.add(d) })
    let html = ""
    for (let i = 0; i < this.range; i++) html += `<div data-cell="1" data-day="${i}" ${this.holidays[i] ? `title="${this._esc(this.holidays[i])}"` : ""} class="cs-cell absolute top-0 bottom-0${filled.has(i) ? " cs-filled" : ""}" style="left:${this.pct(i)}px;width:${DAY_W}px"></div>`
    return html
  }

  // A tiny corner mark on every day of the row that carries a note.
  _noteMarksHtml(pid, tid) {
    let html = ""
    for (let i = 0; i < this.range; i++) if (this._noteMap[`${pid}:${tid}:${i}`]) html += `<div class="cs-note" data-note-day="${i}" style="left:${this.pct(i) + DAY_W - 10}px"></div>`
    return html
  }

  _noteAt(pid, tid, day) { return (this._noteMap && this._noteMap[`${pid}:${tid}:${day}`]) || null }

  // ── Hover card: what a bar is, and the day's note ─────────────────

  boardHover(e) {
    if (!this.hasTipTarget) return
    if (this._paint || this._move || (this.hasPopTarget && !this.popTarget.classList.contains("hidden")) || (this.hasPickTarget && !this.pickTarget.classList.contains("hidden"))) { this.hideTip(); return }
    const line = e.target.closest("[data-line-card]")
    if (line) {
      const card = (this._lineCards || [])[Number(line.dataset.lineCard)]
      if (!card) { this.hideTip(); return }
      const key = `line:${line.dataset.lineCard}`
      if (key !== this._tipKey) { this._tipKey = key; this.tipTarget.innerHTML = this._lineCardHtml(card) }
      this.tipTarget.classList.add("w-80"); this.tipTarget.classList.remove("w-64") // the task list needs the room
      this._placeTip(line, e)
      return
    }
    this.tipTarget.classList.add("w-64"); this.tipTarget.classList.remove("w-80")
    let block = null, note = null, day = null
    const bar = e.target.closest("[data-bar-id]")
    const cell = e.target.closest("[data-cell]"), track = cell && cell.closest("[data-track]")
    if (bar) block = this.blocks.find((b) => b.id === Number(bar.dataset.barId)) || null
    else if (track) {
      const ctx = this._rowCtx(track)
      day = Number(cell.dataset.day)
      block = ctx.blockAt(day)
      note = ctx.tid !== null ? this._noteAt(ctx.pid, ctx.tid, day) : (block ? this._noteAt(ctx.pid, block.trade_id, day) : null)
    }
    if (!block && !note) { this.hideTip(); return }
    const key = `${block ? block.id : "-"}:${note ? note.id : "-"}:${day}`
    if (key !== this._tipKey) { this._tipKey = key; this.tipTarget.innerHTML = this._tipHtml(block, note, day) }
    this._placeTip(bar || cell, e)
  }

  hideTip() { if (this.hasTipTarget) this.tipTarget.classList.add("hidden"); this._tipKey = null }

  _tipHtml(block, note, day) {
    const parts = []
    if (block) {
      const t = this.trades.find((x) => x.id === block.trade_id) || {}, p = this.jobs.find((x) => x.id === block.job_id) || {}
      const s = this._day(block.start_on), e = this._day(block.end_on) + 1, days = e - s
      let work = 0; for (let d = s; d < e; d++) if (!this._isOff(d)) work++
      const crews = Number(t.crews) || 1
      parts.push(`<div class="flex items-center gap-2"><span class="w-3 h-3 rounded-[3px] shrink-0" style="background:${t.color || "#999"}"></span><span class="font-semibold text-gray-800 truncate">${this._esc(t.name || "")}</span>${t.sub ? `<span class="text-gray-500 border border-gray-300 rounded px-1 leading-4 shrink-0">${this._t("tip-sub")}</span>` : ""}</div>`)
      parts.push(`<div class="text-gray-600 truncate">${this._esc(p.name || "")}${block.area ? " · " + this._esc(block.area) : ""}</div>`)
      parts.push(`<div class="text-gray-600">${this._fmt(s)} – ${this._fmtEnd(e)} · ${days} ${days === 1 ? this._t("tip-day") : this._t("tip-days")} ${this._t("tip-in-row")}${work !== days ? ` (${work} ${this._t("tip-working")})` : ""}</div>`)
      const st = this._rowStats(block.job_id, block.trade_id)
      if (st.blocks > 1) parts.push(`<div class="text-gray-600">${this._statsText(st)}</div>`)
      const per = Number(t.people) || 0, here = Number(block.crews) || 1
      parts.push(`<div class="text-gray-600">${here} ${this._t("tip-of")} ${crews} ${crews === 1 ? this._t("tip-crew-here") : this._t("tip-crews-here")}${per ? ` · ${here * per} ${this._t("tip-people")}` : ""}</div>`)
    }
    if (day !== null && this.holidays[day]) parts.push(`<div class="text-purple-700">${this._esc(this.holidays[day])}</div>`)
    if (note) parts.push(`<div class="${block ? "mt-1.5 pt-1.5 border-t border-gray-200" : ""}"><div class="font-semibold text-gray-800">${this._t("tip-note")} · ${this._fmt(this._day(note.on))}</div><div class="text-gray-700 whitespace-pre-wrap break-words">${this._esc(note.body)}</div></div>`)
    return parts.join("")
  }

  // One trade on one job: days across all its blocks, and the longest run in a row.
  _rowStats(pid, tid) {
    const bl = this.blocks.filter((b) => b.job_id === pid && b.trade_id === tid)
    let days = 0, longest = 0
    bl.forEach((b) => { const n = this._day(b.end_on) - this._day(b.start_on) + 1; days += n; if (n > longest) longest = n })
    return { blocks: bl.length, days, longest }
  }

  _statsText(st, withJob = true) {
    if (!st.blocks) return ""
    return `${st.days} ${st.days === 1 ? this._t("tip-day") : this._t("tip-days")}${withJob ? " " + this._t("tip-on-job") : ""} · ${st.blocks} ${st.blocks === 1 ? this._t("tip-block") : this._t("tip-blocks")}${st.blocks > 1 ? ` · ${this._t("tip-longest")} ${st.longest}` : ""}`
  }

  _placeTip(anchorEl, e) {
    const tip = this.tipTarget
    tip.classList.remove("hidden")
    const r = anchorEl.getBoundingClientRect(), w = tip.offsetWidth || 256, h = tip.offsetHeight || 80
    const vw = window.innerWidth || 1200, vh = window.innerHeight || 800
    tip.style.left = `${Math.max(8, Math.min((e && e.clientX ? e.clientX : r.left) - 24, vw - w - 8))}px`
    tip.style.top = `${r.bottom + h + 14 > vh ? Math.max(8, r.top - h - 8) : r.bottom + 6}px`
  }

  // The same squares on the Crews tab, just to look at.
  _gridHtml() {
    let html = ""
    for (let i = 0; i < this.range; i++) html += `<div class="cs-grid absolute top-0 bottom-0 ${this.holidays[i] ? "" : "pointer-events-none"}" ${this.holidays[i] ? `title="${this._esc(this.holidays[i])}"` : ""} style="left:${this.pct(i)}px;width:${DAY_W}px"></div>`
    return html
  }

  _barHtml(b, overlay) {
    return `<div data-bar-id="${b.id}" class="cs-bar absolute rounded-[3px] text-[13px] font-medium overflow-hidden whitespace-nowrap text-ellipsis box-border px-1.5 ${overlay ? "pointer-events-none" : ""}" style="left:${b.left}px;width:${b.width}px;top:${b.top}px;height:${b.h}px;line-height:${b.h}px;background:${b.color};color:${b.text};opacity:${b.done ? 0.55 : 1};box-shadow:inset 0 -1px 0 rgba(0,0,0,.18)">${this._esc(b.label)}</div>`
  }

  _crewLaneHtml(l) {
    const hl = this.state.showConflicts && l.conflicts.some((c) => this.state.highlight === null || c.key === this.state.highlight)
    return `<div class="flex border-b border-gray-200" style="background:${hl ? "var(--color-amber-50, #fffbeb)" : "var(--color-white, #fff)"}">
      <div style="width:var(--cs-label-w);height:${l.height}px;border-left:3px solid ${hl ? "#c2410c" : "transparent"};background:${hl ? "var(--color-amber-50, #fffbeb)" : "var(--color-white, #fff)"}" class="shrink-0 sticky left-0 z-[2] border-r border-gray-200 box-border py-2 pr-3 pl-[9px] flex flex-col justify-center gap-0.5">
        <div class="text-[13px] font-medium truncate" style="color:${l.muted ? "#9b9b9b" : "#1f2937"}">${this._esc(l.name)}</div>
        <div class="text-[13px] text-gray-400 truncate">${this._esc(l.meta)}</div>
      </div>
      <div class="relative shrink-0" style="height:${l.height}px;width:${this.trackW}px">
        ${this._trackExtras()}
        ${this._gridHtml()}
        ${l.bars.map((b) => this._barHtml(b, false)).join("")}
        ${l.conflicts.map((c) => `<div class="absolute top-0 bottom-0 box-border pointer-events-none" style="left:${c.left}px;width:${c.width}px;background:repeating-linear-gradient(135deg, rgba(208,1,27,0.22) 0 5px, rgba(208,1,27,0.05) 5px 10px);border-left:2px solid #d0011b;border-right:2px solid #d0011b"></div><div class="absolute h-[18px] px-1.5 rounded bg-red-600 text-white text-[13px] font-semibold leading-[18px] whitespace-nowrap pointer-events-none" style="bottom:3px;left:${c.left}px;margin-left:3px">${this._esc(c.label)}</div>`).join("")}
      </div></div>`
  }

  _noteRow(text) {
    return `<div class="flex border-b border-gray-200"><div style="width:var(--cs-label-w);height:52px" class="shrink-0 sticky left-0 z-[2] bg-white border-r border-gray-200"></div><div class="relative flex items-center px-3 text-[13px] italic text-gray-400 sticky" style="height:52px;left:var(--cs-label-w)">${this._esc(text)}</div></div>`
  }

  _jobHtml(p, activeTrades, mkBar, stack) {
    const byId = {}; this.trades.forEach((t) => { byId[t.id] = t })
    const order = (p.rows || []).slice()
    this.trades.forEach((t) => { if (!order.includes(t.id) && this.blocks.some((b) => b.job_id === p.id && b.trade_id === t.id)) order.push(t.id) })
    const tradeIds = new Set(order)
    const rowsAll = order.map((id) => byId[id]).filter(Boolean)
    const rows = rowsAll.filter((t) => activeTrades.some((a) => a.id === t.id))
    const labelHtml = `${this._pickBox(p)}<span draggable="true" data-job-handle="${p.id}" title="Drag to reorder jobs" class="cs-grip text-gray-300 select-none text-[14px] leading-none cursor-grab shrink-0">⠿</span><span class="w-2.5 h-2.5 rounded-full shrink-0" style="background:${p.color}"></span><span class="font-semibold text-gray-800 whitespace-nowrap truncate min-w-0">${this._esc(p.name)}</span><span class="ml-auto flex items-center gap-2 shrink-0">${rowsAll.length ? `<span class="text-gray-400 whitespace-nowrap" title="${rowsAll.length} ${rowsAll.length === 1 ? "trade" : "trades"}">${rowsAll.length}</span>` : ""}${this._renameIcon(p)}${this._linkIcon(p)}</span>`
    const head = `<div data-job-head="${p.id}" title="${this._esc(this.state.collapsed.has(p.id) ? this._t("expand-job") : this._t("collapse-job"))}" class="flex h-[30px] bg-gray-100 border-b border-gray-200 text-[13px] cursor-pointer" style="min-width:${this.state.labelW + this.trackW}px"><div class="sticky left-0 flex items-center gap-2 px-3 shrink-0 box-border overflow-hidden" style="width:var(--cs-label-w)">${labelHtml}</div></div>`
    let html = head
    if (!rows.length && rowsAll.length) html += this._noteRow("No work for the picked trades")
    rows.forEach((t) => {
      const bars = []
      this.blocks.filter((b) => b.job_id === p.id && b.trade_id === t.id).forEach((b) => { const bar = mkBar(b, p, t, t.color, t.text, b.area || "", DAY_W - 4); if (bar) bars.push(bar) })
      const nrows = stack(bars, 2, DAY_W)
      const lines = this._tradeLines(p.id, t.id)
      const h = nrows * DAY_W + this._baselinePad(lines.length)
      const lit = (this.conflictList || []).filter((c) => c.tradeId === t.id && c.pids.includes(p.id) && this.state.showConflicts && (this.state.highlight === null || this.state.highlight === c.key))
      html += `<div data-row="1" data-row-pid="${p.id}" data-row-tid="${t.id}" class="flex border-b border-gray-200 bg-white"${lit.length ? ' style="background:var(--color-red-50, #fff5f5)"' : ""}>
        <div draggable="true" data-row-handle="1" style="width:var(--cs-label-w);height:${h}px" class="cs-row-label shrink-0 sticky left-0 z-[2] bg-white border-r border-gray-200 box-border py-1 pr-2 pl-2 flex items-center gap-2">
          <span title="Drag to reorder" class="cs-grip text-gray-300 select-none text-[14px] leading-none">⠿</span>
          <span class="w-3 h-3 rounded-[3px] shrink-0" style="background:${t.color}"></span>
          <span class="text-[13px] font-medium truncate flex-1 ${lit.length ? "text-red-700" : "text-gray-800"}">${this._esc(t.name)}</span>
          ${lit.length ? `<span class="text-[13px] font-semibold text-red-700 whitespace-nowrap">${lit.length} ${lit.length === 1 ? this._t("conflict") : this._t("conflicts")}</span>` : ""}
          ${bars.length ? "" : `<button type="button" data-remove-row="${t.id}" data-job-id="${p.id}" title="Remove this row" class="text-gray-300 hover:text-red-600 cursor-pointer">×</button>`}
        </div>
        <div data-track="1" data-job-id="${p.id}" data-trade-id="${t.id}" class="relative shrink-0" style="height:${h}px;width:${this.trackW}px">
          ${this._trackExtras()}
          ${this._cellsHtml(bars)}
          ${bars.map((b) => this._barHtml(b, true)).join("")}
          ${lit.map((c) => `<div class="cs-conflict absolute top-0 bottom-0 box-border pointer-events-none" style="left:${c.left}px;width:${c.width}px;background:repeating-linear-gradient(135deg, rgba(208,1,27,0.28) 0 5px, rgba(208,1,27,0.06) 5px 10px);border-left:2px solid #d0011b;border-right:2px solid #d0011b"></div>`).join("")}
          ${this._linesHtml(lines, { pid: p.id, tid: t.id })}
          ${this._noteMarksHtml(p.id, t.id)}
        </div></div>`
    })
    // The "new row" line: click a day and pick the trade.
    html += `<div data-job-tail="${p.id}" class="flex border-b border-gray-200 bg-white">
      <div style="width:var(--cs-label-w);height:${DAY_W}px" class="shrink-0 sticky left-0 z-[2] bg-white border-r border-gray-200 box-border pl-6 pr-2 flex items-center gap-2 text-[13px] truncate"><button type="button" data-add-trade="${p.id}" class="font-medium text-brand hover:underline cursor-pointer whitespace-nowrap">+ Add trade</button><span class="text-gray-400 italic truncate">${this._esc(this._t("or-click-day"))}</span></div>
      <div data-newrow="1" data-job-id="${p.id}" class="relative shrink-0" style="height:${DAY_W}px;width:${this.trackW}px">${this._trackExtras()}${this._cellsHtml()}</div></div>`
    return this.state.collapsed.has(p.id) ? this._collapsedHeadHtml(p, this.blocks.filter((b) => b.job_id === p.id && rows.some((t) => t.id === b.trade_id)), labelHtml) : html
  }

  // ── Rows by AREA (his spreadsheet): one row per part of the job, the color says the trade ──

  // The row a block sits on in area mode: its label; with no label, its
  // trade's name when the job has areas, else "" (one row for the whole job).
  _areaKey(pid, b) {
    if (b.area) return b.area
    if (!this.blocks.some((x) => x.job_id === pid && x.area)) return ""
    const t = this.trades.find((x) => x.id === b.trade_id)
    return t ? t.name : ""
  }

  _blockAtArea(pid, area, day) {
    return this.blocks.find((b) => b.job_id === pid && this._areaKey(pid, b).toLowerCase() === area.toLowerCase() && this._day(b.start_on) <= day && this._day(b.end_on) >= day) || null
  }

  // What a track row is: a trade row (rows by trade) or an area row (rows by area).
  _rowCtx(track) {
    const pid = Number(track.dataset.jobId)
    if (track.dataset.tradeId) { const tid = Number(track.dataset.tradeId); return { pid, tid, area: null, blockAt: (d) => this._blockAt(pid, tid, d) } }
    const area = track.dataset.area || ""
    return { pid, tid: null, area, blockAt: (d) => this._blockAtArea(pid, area, d) }
  }

  _jobAreaHtml(p, activeTrades, mkBar, stack) {
    const byId = {}; this.trades.forEach((t) => { byId[t.id] = t })
    const active = new Set(activeTrades.map((t) => t.id))
    const mine = this.blocks.filter((b) => b.job_id === p.id && byId[b.trade_id]).sort((a, b) => this._day(a.start_on) - this._day(b.start_on) || a.id - b.id)
    const keys = this._areaOrder(p.id, mine)
    const hasAreas = mine.some((b) => b.area) || this.areas.some((a) => a.job_id === p.id)
    const shown = keys.filter((k) => { const on = mine.filter((b) => this._areaKey(p.id, b).toLowerCase() === k.toLowerCase()); return !on.length || on.some((b) => active.has(b.trade_id)) })
    const n = keys.length
    const labelHtml = `${this._pickBox(p)}<span draggable="true" data-job-handle="${p.id}" title="Drag to reorder jobs" class="cs-grip text-gray-300 select-none text-[14px] leading-none cursor-grab shrink-0">⠿</span><span class="w-2.5 h-2.5 rounded-full shrink-0" style="background:${p.color}"></span><span class="font-semibold text-gray-800 whitespace-nowrap truncate min-w-0">${this._esc(p.name)}</span><span class="ml-auto flex items-center gap-2 shrink-0">${n ? `<span class="text-gray-400 whitespace-nowrap" title="${n} ${n === 1 ? this._t("area-word") : this._t("areas-word")}">${n}</span>` : ""}${this._renameIcon(p)}${this._linkIcon(p)}</span>`
    const head = `<div data-job-head="${p.id}" title="${this._esc(this.state.collapsed.has(p.id) ? this._t("expand-job") : this._t("collapse-job"))}" class="flex h-[30px] bg-gray-100 border-b border-gray-200 text-[13px] cursor-pointer" style="min-width:${this.state.labelW + this.trackW}px"><div class="sticky left-0 flex items-center gap-2 px-3 shrink-0 box-border overflow-hidden" style="width:var(--cs-label-w)">${labelHtml}</div></div>`
    let html = head
    if (!shown.length && keys.length) html += this._noteRow("No work for the picked trades")
    shown.forEach((k) => {
      const bars = []
      mine.filter((b) => this._areaKey(p.id, b).toLowerCase() === k.toLowerCase() && active.has(b.trade_id)).forEach((b) => { const t = byId[b.trade_id]; const bar = mkBar(b, p, t, t.color, t.text, t.name, DAY_W - 4); if (bar) bars.push(bar) })
      const nrows = stack(bars, 2, DAY_W)
      const lines = this._areaLines(p.id, k, active)
      const h = nrows * DAY_W + this._baselinePad(lines.length)
      const tids = new Set(bars.map((b) => b.tradeId))
      const lit = (this.conflictList || []).filter((c) => tids.has(c.tradeId) && c.pids.includes(p.id) && this.state.showConflicts && (this.state.highlight === null || this.state.highlight === c.key))
      const label = k === "" ? (hasAreas ? this._t("no-area") : this._t("whole-job")) : k
      const saved = k !== "" && this.areas.some((a) => a.job_id === p.id && a.name.toLowerCase() === k.toLowerCase())
      html += `<div data-row="1" data-row-pid="${p.id}" data-row-area="${this._esc(k)}" class="flex border-b border-gray-200 bg-white"${lit.length ? ' style="background:var(--color-red-50, #fff5f5)"' : ""}>
        <div ${k === "" ? "" : 'draggable="true" data-row-handle="1"'} style="width:var(--cs-label-w);height:${h}px" class="cs-row-label shrink-0 sticky left-0 z-[2] bg-white border-r border-gray-200 box-border py-1 pr-2 pl-2 flex items-center gap-2">
          <span title="Drag to reorder" class="cs-grip text-gray-300 select-none text-[14px] leading-none ${k === "" ? "invisible" : ""}">⠿</span>
          <span class="text-[13px] font-medium truncate flex-1 ${lit.length ? "text-red-700" : (k === "" ? "text-gray-500 italic" : "text-gray-800")}">${this._esc(label)}</span>
          ${lit.length ? `<span class="text-[13px] font-semibold text-red-700 whitespace-nowrap">${lit.length} ${lit.length === 1 ? this._t("conflict") : this._t("conflicts")}</span>` : ""}
          ${bars.length || !saved ? "" : `<button type="button" data-remove-area="${this._esc(k)}" data-job-id="${p.id}" title="Remove this area" class="text-gray-300 hover:text-red-600 cursor-pointer">×</button>`}
        </div>
        <div data-track="1" data-job-id="${p.id}" data-area="${this._esc(k)}" class="relative shrink-0" style="height:${h}px;width:${this.trackW}px">
          ${this._trackExtras()}
          ${this._cellsHtml(bars)}
          ${bars.map((b) => this._barHtml(b, true)).join("")}
          ${lit.map((c) => `<div class="cs-conflict absolute top-0 bottom-0 box-border pointer-events-none" style="left:${c.left}px;width:${c.width}px;background:repeating-linear-gradient(135deg, rgba(208,1,27,0.28) 0 5px, rgba(208,1,27,0.06) 5px 10px);border-left:2px solid #d0011b;border-right:2px solid #d0011b"></div>`).join("")}
          ${this._linesHtml(lines, { pid: p.id, area: k })}
          ${this._noteMarksAreaHtml(p.id, k)}
        </div></div>`
    })
    // The "new row" line: name an area, or click a day and pick the trade.
    html += `<div data-job-tail="${p.id}" class="flex border-b border-gray-200 bg-white">
      <div style="width:var(--cs-label-w);height:${DAY_W}px" class="shrink-0 sticky left-0 z-[2] bg-white border-r border-gray-200 box-border pl-6 pr-2 flex items-center gap-2 text-[13px] truncate"><button type="button" data-add-area="${p.id}" class="font-medium text-brand hover:underline cursor-pointer whitespace-nowrap">+ Add area</button><span class="text-gray-400 italic truncate">${this._esc(this._t("or-click-day"))}</span></div>
      <div data-newrow="1" data-job-id="${p.id}" data-new-area="1" class="relative shrink-0" style="height:${DAY_W}px;width:${this.trackW}px">${this._trackExtras()}${this._cellsHtml()}</div></div>`
    return this.state.collapsed.has(p.id) ? this._collapsedHeadHtml(p, mine.filter((b) => active.has(b.trade_id)), labelHtml) : html
  }

  // A job's area rows in order: the saved areas first (the sheet's rows, empty ones
  // too), then any label on a block that isn't saved yet, as it first appears.
  _areaOrder(pid, mine) {
    const keys = []
    const has = (k) => keys.some((x) => x.toLowerCase() === k.toLowerCase())
    this.areas.filter((a) => a.job_id === pid).sort((a, b) => a.position - b.position || a.id - b.id).forEach((a) => { if (!has(a.name)) keys.push(a.name) })
    // Rows that aren't saved areas keep a fixed place too: trade rows follow the
    // job's trade order, other labels go by name — never by date, so nothing jumps.
    const p = this.jobs.find((x) => x.id === pid)
    const tradeOrder = ((p && p.rows) || []).slice(); this.trades.forEach((t) => { if (!tradeOrder.includes(t.id)) tradeOrder.push(t.id) })
    const rest = []
    mine.forEach((b) => { const k = this._areaKey(pid, b); if (has(k) || rest.some((x) => x.key.toLowerCase() === k.toLowerCase())) return; rest.push({ key: k, at: b.area ? Infinity : tradeOrder.indexOf(b.trade_id) }) })
    rest.sort((a, b) => a.at - b.at || a.key.localeCompare(b.key)).forEach((x) => keys.push(x.key))
    return keys
  }

  _rememberArea(area) { if (area && !this.areas.some((a) => a.job_id === area.job_id && a.name.toLowerCase() === area.name.toLowerCase())) this.areas.push(area) }

  // The job's areas straight from the server when it re-ordered them (a new one slotted in), else just the one.
  _takeAreas(pid, res) {
    if (res.areas) this.areas = this.areas.filter((a) => a.job_id !== pid).concat(res.areas)
    else this._rememberArea(res.area)
  }

  _placedText(res) {
    if (res.placed === "first") return this._t("area-placed-first")
    if (res.placed === "after" && res.placed_after) return `${this._t("area-placed-after")} ${res.placed_after}`
    if (res.placed === "last") return this._t("area-placed-last")
    return this._t("saved")
  }

  // Note marks on an area row: a note shows on the day its trade has a block there.
  _noteMarksAreaHtml(pid, area) {
    let html = ""
    ;(this.notes || []).forEach((nt) => {
      if (nt.job_id !== pid) return
      const d = this._day(nt.on)
      if (d < 0 || d >= this.range) return
      const b = this._blockAt(pid, nt.trade_id, d)
      if (b && this._areaKey(pid, b).toLowerCase() === area.toLowerCase()) html += `<div class="cs-note" data-note-day="${d}" style="left:${this.pct(d) + DAY_W - 10}px"></div>`
    })
    return html
  }

  rowsByTrade() { this._setRows("trade") }
  rowsByArea() { this._setRows("area") }
  // Fold a job's rows away behind its title row (Phelipe 2026-09-15); the
  // choice rides along with the other picks, so it sticks per user.
  _toggleJob(pid) {
    if (this.state.collapsed.has(pid)) this.state.collapsed.delete(pid); else this.state.collapsed.add(pid)
    this.closePop(); this.closePick(); this.hideTip(); this.render(); this._persist()
    this._track("Crew Job Collapsed", { collapsed: this.state.collapsed.has(pid) })
  }

  // One link in the corner (Phelipe 2026-09-15): "Collapse all" until every
  // job is folded, then "Expand all".
  _allFolded() { return this.jobs.length > 0 && this.jobs.every((j) => this.state.collapsed.has(j.id)) }
  toggleAll() { this._allFolded() ? this.expandAll() : this.collapseAll() }

  collapseAll() {
    this.state.collapsed = new Set(this.jobs.map((j) => j.id))
    this.closePop(); this.closePick(); this.hideTip(); this.render(); this._persist()
    this._track("Crew Jobs Collapsed All", { jobs: this.jobs.length })
  }

  expandAll() {
    this.state.collapsed = new Set()
    this.closePop(); this.closePick(); this.hideTip(); this.render(); this._persist()
    this._track("Crew Jobs Expanded All", { jobs: this.jobs.length })
  }

  _setRows(v) { if (this.state.rowsBy === v) return; this.state.rowsBy = v; this.closePop(); this.closePick(); this.hideTip(); this.render(); this._persist(); this._track("Crew Rows Grouped", { by: v }) }
  _styleSeg(el, on) { el.classList.toggle("bg-brand", on); el.classList.toggle("text-white", on); el.classList.toggle("bg-white", !on); el.classList.toggle("text-gray-600", !on) }

  // Right-click on an area name: rename it, or remove it once it is empty.
  _openAreaMenu(row, anchorEl) {
    if (!row || !this.hasAreaMenuTarget) return
    this.closePop(); this.closePick(); this.closeRowMenu(); this.hideTip()
    const pid = Number(row.dataset.rowPid), name = row.dataset.rowArea || ""
    const p = this.jobs.find((x) => x.id === pid)
    if (!p || !name) return
    const count = this.blocks.filter((b) => b.job_id === pid && this._areaKey(pid, b).toLowerCase() === name.toLowerCase()).length
    this._areaMenuFor = { pid, name, count }
    this.areaMenuTitleTarget.textContent = `${p.name} · ${name}`
    this.areaMenuMetaTarget.textContent = count ? `${count} ${count === 1 ? this._t("tip-block") : this._t("tip-blocks")}` : this._t("area-empty")
    this.areaMenuNameTarget.value = name
    this.areaMenuRemoveTarget.disabled = count > 0
    this.areaMenuHintTarget.textContent = count > 0 ? this._t("area-clear-first") : ""
    this._place(this.areaMenuTarget, anchorEl)
    setTimeout(() => { this.areaMenuNameTarget.focus(); this.areaMenuNameTarget.select() }, 0)
  }

  closeAreaMenu() { if (this.hasAreaMenuTarget) this.areaMenuTarget.classList.add("hidden"); this._areaMenuFor = null }
  areaNameKey(e) { if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur() } else if (e.key === "Escape") { e.stopPropagation(); this.closeAreaMenu() } }

  async renameArea() {
    const m = this._areaMenuFor
    if (!m) return
    const to = this.areaMenuNameTarget.value.trim()
    if (!to || to === m.name) return
    const res = await this._json(`${this.areasUrlValue}/rename`, "PATCH", { job_id: m.pid, name: m.name, new_name: to })
    if (!res || !res.ok) return
    const same = (a, b) => a.toLowerCase() === b.toLowerCase()
    const hit = this.blocks.filter((b) => b.job_id === m.pid && same(this._areaKey(m.pid, b), m.name))
    hit.forEach((b) => { b.area = res.name })
    this.areas = this.areas.filter((a) => !(a.job_id === m.pid && (same(a.name, m.name) || (res.area && a.id === res.area.id))))
    if (res.area) this.areas.push(res.area)
    this.closeAreaMenu(); this._toast(this._t("saved")); this._track("Crew Area Renamed", { job_id: m.pid }); this.render()
  }

  areaMenuRemove() { const m = this._areaMenuFor; if (!m || m.count) return; this.closeAreaMenu(); this._removeArea(m.pid, m.name) }

  // The little chain on a job header: purple when the job has no project yet, grey once matched.
  _linkIcon(p) {
    return `<button type="button" data-link-job="${p.id}" title="${this._esc(this._t(p.crew_only ? "crew-only" : "job-matched"))}" class="inline-flex items-center justify-center w-5 h-5 rounded ${p.crew_only ? "text-gray-300 hover:text-gray-500 hover:bg-gray-200" : "text-purple-600 hover:bg-purple-100"} cursor-pointer">${this._linkSvg()}</button>`
  }

  _box(on) {
    return `<span class="inline-flex items-center justify-center w-4 h-4 rounded border ${on ? "bg-brand border-brand" : "bg-white border-gray-300"} shrink-0">${on ? '<svg width="11" height="11" viewBox="0 0 12 12" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 6.5l2.5 2.5L10 3.5"/></svg>' : ""}</span>`
  }

  // Untick a job on its title to take it off the view (Phelipe 2026-09-17).
  // Same tick box and same switch as the Jobs picker, so they stay in step.
  _pickBox(p) {
    return `<button type="button" data-job-id="${p.id}" data-action="click->crew-schedule#toggleJob" title="${this._esc(this._t("hide-job"))}" class="shrink-0 flex items-center cursor-pointer opacity-70 hover:opacity-100 transition-opacity"><span class="inline-flex items-center justify-center w-3 h-3 rounded-[2px] bg-gray-600 border border-gray-600 text-white shrink-0"><svg width="8" height="8" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M2 6.5l2.5 2.5L10 3.5"/></svg></span></button>`
  }

  _renameIcon(p) {
    return `<button type="button" data-rename-job="${p.id}" title="${this._esc(this._t("rename-job"))}" class="inline-flex items-center justify-center w-5 h-5 rounded text-gray-300 hover:text-gray-600 hover:bg-gray-200 cursor-pointer"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg></button>`
  }

  _linkSvg() {
    return '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>'
  }

  // ── Match a job to a project, change it, or unmatch it ─────────────

  _openLinkJob(jid) {
    const job = this.jobs.find((x) => x.id === jid)
    if (!job || !this.hasLinkJobModalTarget) return
    this._linkJobFor = jid
    this.linkJobTitleTarget.textContent = job.name
    const projects = (this.jobsValue || []).filter((j) => j.project_id && j.id !== jid).sort((a, b) => a.name.localeCompare(b.name))
    this.linkJobSelectTarget.innerHTML = `<option value="">${this._esc(this._t("pick-project"))}</option>` +
      projects.map((j) => `<option value="${j.project_id}">${this._esc(j.name)}</option>`).join("")
    if (this.hasLinkJobCurrentTarget) { this.linkJobCurrentTarget.classList.toggle("hidden", !!job.crew_only); this.linkJobCurrentTarget.textContent = job.crew_only ? "" : `${this._t("job-linked")}: ${job.name}` }
    if (this.hasLinkJobUnmatchTarget) this.linkJobUnmatchTarget.classList.toggle("hidden", !!job.crew_only)
    // Taking the project's name is the default, the way it always worked.
    if (this.hasLinkJobUseNameTarget) this.linkJobUseNameTarget.checked = true
    this.linkJobModalTarget.classList.remove("hidden")
  }

  closeLinkJob() { if (this.hasLinkJobModalTarget) this.linkJobModalTarget.classList.add("hidden"); this._linkJobFor = null }

  async linkJob(e) {
    if (e && e.preventDefault) e.preventDefault()
    const jid = this._linkJobFor, projectId = Number(this.linkJobSelectTarget.value)
    if (!jid || !projectId) return
    const useProjectName = this.hasLinkJobUseNameTarget ? this.linkJobUseNameTarget.checked : true
    const res = await this._json(this.linkJobUrlValue, "PATCH", { job_id: jid, project_id: projectId, use_project_name: useProjectName })
    if (!res || !res.ok) return
    this.closeLinkJob()
    // The job keeps its place on the board under its new identity.
    this.state.jobIds = Array.from(new Set(this.state.jobIds.map((id) => (id === res.old_id ? res.job.id : id))))
    this.jobsValue = (this.jobsValue || []).filter((j) => j.id !== res.old_id && j.id !== res.job.id).concat([{ id: res.job.id, name: res.job.name, crew_only: false, project_id: res.job.project_id, project_name: res.job.project_name, own_name: res.job.own_name }])
    this._toast(this._t("job-linked")); this._track("Crew Job Linked", { job_id: res.job.id })
    await this.load()
  }

  async unlinkJob() {
    const jid = this._linkJobFor
    if (!jid) return
    const res = await this._json(this.unlinkJobUrlValue, "PATCH", { job_id: jid })
    if (!res || !res.ok) return
    this.closeLinkJob()
    this.jobsValue = (this.jobsValue || []).map((j) => (j.id === jid ? { id: j.id, name: res.job.name, crew_only: true, project_id: null } : j))
    this._toast(this._t("job-unlinked")); this._track("Crew Job Unlinked", { job_id: jid })
    await this.load()
  }

  // ── Rename a job (Phelipe 2026-09-17) ──────────────────────────────

  _openRenameJob(jid) {
    const job = (this.jobsValue || []).find((x) => x.id === jid) || (this.jobs || []).find((x) => x.id === jid)
    if (!job || !this.hasRenameJobModalTarget) return
    this._renameJobFor = jid
    this.renameJobInputTarget.value = job.name || ""
    // A matched job may keep a name of its own; empty puts the project's back.
    if (this.hasRenameJobHintTarget) {
      const matched = !job.crew_only && job.project_name
      this.renameJobHintTarget.classList.toggle("hidden", !matched)
      if (matched) this.renameJobHintTarget.textContent = `${this._t("rename-matched-hint")} ${job.project_name}`
    }
    this.renameJobModalTarget.classList.remove("hidden")
    setTimeout(() => { this.renameJobInputTarget.focus(); this.renameJobInputTarget.select() }, 0)
  }

  closeRenameJob() { if (this.hasRenameJobModalTarget) this.renameJobModalTarget.classList.add("hidden"); this._renameJobFor = null }

  async renameJob(e) {
    if (e && e.preventDefault) e.preventDefault()
    const jid = this._renameJobFor
    if (!jid) return
    const res = await this._json(this.renameJobUrlValue, "PATCH", { job_id: jid, name: this.renameJobInputTarget.value.trim() })
    if (!res || !res.ok) return
    this.closeRenameJob()
    this.jobsValue = (this.jobsValue || []).map((j) => (j.id === jid ? { ...j, name: res.job.name, own_name: res.job.own_name } : j))
    this._toast(this._t("job-renamed")); this._track("Crew Job Renamed", { job_id: jid })
    await this.load()
  }

  // ── Add a job that has no project yet (Phelipe 2026-09-16) ─────────

  openAddJob() {
    if (!this.hasAddJobModalTarget) return
    this.closeMenus()
    this.addJobInputTarget.value = ""
    this.addJobModalTarget.classList.remove("hidden")
    setTimeout(() => this.addJobInputTarget.focus(), 0)
  }
  closeAddJob() { if (this.hasAddJobModalTarget) this.addJobModalTarget.classList.add("hidden") }

  async addJob(e) {
    if (e && e.preventDefault) e.preventDefault()
    const name = this.addJobInputTarget.value.trim()
    if (!name) { this.addJobInputTarget.focus(); return }
    const res = await this._json(this.addJobUrlValue, "POST", { name })
    if (!res || !res.ok) return
    this.closeAddJob()
    const job = { id: res.job.id, name: res.job.name, crew_only: true, project_id: null }
    if (!(this.jobsValue || []).some((j) => j.id === job.id)) this.jobsValue = (this.jobsValue || []).concat([job])
    if (!this.state.jobIds.includes(job.id)) this.state.jobIds = this.state.jobIds.concat([job.id])
    this._toast(this._t(res.existed ? "job-exists" : "job-added"))
    if (!res.existed) this._track("Crew Job Added", { job_id: job.id })
    await this.load()
  }

  // ── Compare with the project schedule (Phelipe 2026-09-16) ─────────
  // A thin line per trade under each row, in the trade's colour: when the
  // linked project's schedule says that trade's work happens there. The AI
  // pairing runs in the background; we poll for it.

  toggleCompare() {
    this.state.compare = this.compareBoxTarget.checked
    this._comparePolls = 0; this._compareAnnounce = true // say so if nothing can be compared — only when the switch was just flipped, since it is on by default
    this.load(); this._track("Crew Compare Toggled", { on: this.state.compare })
  }

  async refreshCompare() {
    const linked = this.jobs.filter((j) => j.project_id)
    if (!linked.length) { this._toast(this._t("compare-none")); return }
    for (const j of linked) await this._json(this.refreshCompareUrlValue, "POST", { job_id: j.id })
    this._toast(this._t("compare-refreshed")); this._comparePolls = 0
    setTimeout(() => this.load(), 3000)
  }

  _pollComparison() {
    clearTimeout(this._compareTimer)
    const c = this.comparison
    if (!this.state.compare || !c) return
    if (c.pending && c.pending.length) {
      if (!this._comparePolls) this._toast(this._t("compare-pending"))
      this._comparePolls = (this._comparePolls || 0) + 1
      if (this._comparePolls <= 20) this._compareTimer = setTimeout(() => this.load(), 4000)
    } else if (this._compareAnnounce && !Object.keys(c.lines || {}).length && (c.no_schedule || []).length === this.jobs.length && this.jobs.length) {
      this._compareAnnounce = false; this._toast(this._t("compare-none"))
    }
  }

  // Row space for the lines: one 4px line per trade, 5px apart, 3px of air.
  _baselinePad(n) { return this.state.compare ? 3 + 5 * Math.max(1, n || 0) : 0 }

  // The lines under an area row: one per trade planned there (among the
  // picked trades), with the spans the project schedule has for that trade.
  _areaLines(pid, area, active) {
    const c = this.comparison
    if (!this.state.compare || !c || !c.lines) return []
    const byTrade = (c.lines[pid] || {})[area.toLowerCase()] || {}
    return this.trades.filter((t) => byTrade[t.id] && (!active || active.has(t.id))).map((t) => ({ trade: t, spans: byTrade[t.id] }))
  }

  // The line under a trade row: that trade's work anywhere on the job.
  _tradeLines(pid, tid) {
    const c = this.comparison
    if (!this.state.compare || !c || !c.trades) return []
    const spans = (c.trades[pid] || {})[tid] || []
    const t = this.trades.find((x) => x.id === tid)
    return spans.length && t ? [{ trade: t, spans }] : []
  }

  // One hit area per span, the trade's colour as a 4px bar at its foot and a
  // dark shade over the part each task is reported done; hover for the card.
  _linesHtml(lines, ctx) {
    return lines.map((l, i) => l.spans.map((sp) => {
      const s = Math.max(0, this._day(sp.s)), e = Math.min(this.range - 1, this._day(sp.e))
      if (e < 0 || s >= this.range || e < s) return ""
      const tasks = (sp.tasks || []).map((t) => (typeof t === "string" ? { name: t } : t))
      const done = tasks.map((t) => {
        if (!t.s || !t.e || !(Number(t.done) > 0)) return ""
        const ts = this._day(t.s), te = this._day(t.e), days = te - ts + 1
        const to = Math.min(te, ts + Math.round(days * Number(t.done) / 100) - 1)
        const a = Math.max(ts, s), b = Math.min(to, e)
        if (b < a) return ""
        return `<div class="absolute rounded-full pointer-events-none" style="left:${(a - s) * DAY_W}px;width:${(b - a + 1) * DAY_W - 4}px;bottom:2px;height:4px;background:rgba(0,0,0,.45)"></div>`
      }).join("")
      const idx = this._lineCards.push({ trade: l.trade, span: sp, tasks, ctx }) - 1
      return `<div data-line-card="${idx}" class="cs-baseline absolute pointer-events-auto cursor-default" style="left:${s * DAY_W + 2}px;width:${(e - s + 1) * DAY_W - 4}px;bottom:${5 * i}px;height:8px">
        <div class="absolute left-0 right-0 rounded-full pointer-events-none" style="bottom:2px;height:4px;background:${l.trade.color};box-shadow:inset 0 0 0 1px rgba(0,0,0,.18)"></div>${done}</div>`
    }).join("")).join("")
  }

  // The hover card for a line: the schedule's tasks behind it (dates, % done)
  // and how the crew plan on this row compares.
  _lineCardHtml(c) {
    const { trade, span, tasks, ctx } = c
    const s = this._day(span.s), e = this._day(span.e) + 1, days = e - s
    const sched = ((this.comparison || {}).schedules || {})[ctx.pid]
    const parts = []
    parts.push(`<div class="flex items-center gap-2"><span class="w-3 h-3 rounded-[3px] shrink-0" style="background:${trade.color}"></span><span class="font-semibold text-gray-800 truncate">${this._esc(trade.name)}</span></div>`)
    parts.push(`<div class="text-gray-600 truncate">${this._t("baseline")}${sched ? " · " + this._esc(sched) : ""}</div>`)
    const pct = tasks.length ? Math.round(tasks.reduce((acc, t) => acc + (Number(t.done) || 0), 0) / tasks.length) : 0
    parts.push(`<div class="text-gray-600">${this._fmtIso(span.s)} – ${this._fmtIso(span.e)} · ${days} ${days === 1 ? this._t("tip-day") : this._t("tip-days")} · ${pct}% ${this._t("tip-done")}</div>`)
    if (tasks.length) {
      parts.push(`<div class="mt-1.5 pt-1.5 border-t border-gray-200 space-y-1">${tasks.map((t) => `<div><div class="flex items-baseline gap-2"><span class="flex-1 min-w-0 truncate text-gray-800 font-medium">${this._esc(t.name)}</span>${t.done !== undefined ? `<span class="whitespace-nowrap ${Number(t.done) >= 100 ? "text-green-700 font-medium" : "text-gray-500"}">${Number(t.done) || 0}% ${this._t("tip-done")}</span>` : ""}</div><div class="text-gray-500 truncate">${t.s ? `${this._fmtIso(t.s)} – ${this._fmtIso(t.e)}` : ""}${t.path ? ` · ${this._esc(t.path)}` : ""}</div></div>`).join("")}</div>`)
    }
    const mine = this.blocks.filter((b) => b.job_id === ctx.pid && b.trade_id === trade.id && (ctx.tid !== undefined || this._areaKey(ctx.pid, b).toLowerCase() === String(ctx.area).toLowerCase()))
    if (mine.length) {
      const cs = mine.reduce((m, b) => (b.start_on < m ? b.start_on : m), mine[0].start_on), ce = mine.reduce((m, b) => (b.end_on > m ? b.end_on : m), mine[0].end_on)
      const diff = this._day(cs) - s
      const how = diff === 0 ? this._t("tip-crew-same") : `${this._t(diff > 0 ? "tip-crew-later" : "tip-crew-earlier").replace("%{n}", Math.abs(diff))}`
      parts.push(`<div class="mt-1.5 pt-1.5 border-t border-gray-200 text-gray-700"><span class="font-semibold text-gray-800">${this._t("tip-crew-plan")}</span> · ${this._fmtIso(cs)} – ${this._fmtIso(ce)}<div class="text-gray-600">${how}</div></div>`)
    } else {
      parts.push(`<div class="mt-1.5 pt-1.5 border-t border-gray-200 text-gray-600">${this._t("tip-no-crew")}</div>`)
    }
    return parts.join("")
  }

  _fmtIso(iso) { const [y, m, d] = String(iso).split("-"); return `${Number(m)}/${Number(d)}/${y}` }

  _openAddArea(pid) {
    const p = this.jobs.find((x) => x.id === pid)
    if (!p || !this.hasAddAreaModalTarget) return
    this._addAreaFor = pid
    this.addAreaTitleTarget.textContent = p.name
    this.addAreaInputTarget.value = ""
    this.addAreaModalTarget.classList.remove("hidden")
    setTimeout(() => this.addAreaInputTarget.focus(), 0)
  }
  closeAddArea() { if (this.hasAddAreaModalTarget) this.addAreaModalTarget.classList.add("hidden"); this._addAreaFor = null }
  async addAreaFromModal(e) {
    if (e && e.preventDefault) e.preventDefault()
    const pid = this._addAreaFor, name = this.hasAddAreaInputTarget ? this.addAreaInputTarget.value.trim() : ""
    if (!pid || !name) return
    const btn = this.addAreaModalTarget.querySelector('button[type="submit"]')
    if (btn) btn.disabled = true
    const res = await this._json(this.areasUrlValue, "POST", { job_id: pid, name })
    if (btn) btn.disabled = false
    if (!res || !res.ok) return
    this._takeAreas(pid, res)
    this.closeAddArea(); this._toast(this._placedText(res)); this._track("Crew Area Added", { job_id: pid, placed: res.placed || "existing" }); this.render()
  }

  async _removeArea(pid, name) {
    const res = await this._json(this.areasUrlValue, "DELETE", { job_id: pid, name })
    if (!res || !res.ok) return
    this.areas = this.areas.filter((a) => !(a.job_id === pid && a.name.toLowerCase() === name.toLowerCase()))
    this._toast(this._t("saved")); this.render()
  }

  // ── Board interactions (Jobs tab: squares, like the spreadsheet) ──

  _blockAt(pid, tid, day) {
    return this.blocks.find((b) => b.job_id === pid && b.trade_id === tid && this._day(b.start_on) <= day && this._day(b.end_on) >= day) || null
  }

  _dayFromEvent(e, track) {
    const r = track.getBoundingClientRect()
    return Math.max(0, Math.min(this.range - 1, Math.floor((e.clientX - r.left) / DAY_W)))
  }

  boardDown(e) {
    this.hideTip()
    if (this._clickTimer) { clearTimeout(this._clickTimer); this._clickTimer = null }
    this._justPainted = false // a new gesture
    if (e.button !== 0 || this.state.tab !== "jobs") return
    const cell = e.target.closest("[data-cell]")
    const track = cell && cell.closest("[data-track]")
    if (!cell || !track) return
    const ctx = this._rowCtx(track), pid = ctx.pid, day = Number(cell.dataset.day)
    const block = ctx.blockAt(day)
    if (block) {
      // Press on a solid block: drag it to other days. A plain click opens its menu.
      const tid = block.trade_id
      const chain = this._chainOf(pid, tid, block, ctx.area)
      const bars = chain.map((b) => ({ el: track.querySelector(`[data-bar-id="${b.id}"]`), s: this._day(b.start_on) })).filter((x) => x.el)
      let working = 0
      chain.forEach((b) => { for (let d = this._day(b.start_on); d <= this._day(b.end_on); d++) if (!this._isOff(d)) working++ })
      this._move = { block, chain, pid, tid, track, bars, working, ghosts: [], anchor: day, cur: day, s: this._day(chain[0].start_on), e: this._day(chain[chain.length - 1].end_on) + 1 }
      e.preventDefault()
      return
    }
    this._paint = { pid, tid: ctx.tid, area: ctx.area, track, anchor: day, cur: day }
    this._previewPaint()
    e.preventDefault()
  }

  // With the switch on, blocks on a row that only weekends and holidays keep apart move as one.
  _chainOf(pid, tid, block, area = null) {
    if (!this.state.skipOff) return [block]
    const row = this.blocks.filter((b) => b.job_id === pid && b.trade_id === tid && (area === null || this._areaKey(pid, b).toLowerCase() === area.toLowerCase())).sort((a, b) => this._day(a.start_on) - this._day(b.start_on))
    const i = row.findIndex((b) => b.id === block.id)
    if (i < 0) return [block]
    const offGap = (a, b) => { for (let d = this._day(a.end_on) + 1; d < this._day(b.start_on); d++) if (!this._isOff(d)) return false; return true }
    let lo = i, hi = i
    while (lo > 0 && offGap(row[lo - 1], row[lo])) lo--
    while (hi < row.length - 1 && offGap(row[hi], row[hi + 1])) hi++
    return row.slice(lo, hi + 1)
  }

  // The dragged run drawn on the days it will actually take, hopping over off days.
  _showGhosts(m, days) {
    this._clearGhosts(m)
    m.bars.forEach((bar) => { bar.el.style.visibility = "hidden" })
    const first = m.bars[0] && m.bars[0].el
    const t = this.trades.find((x) => x.id === m.tid) || {}
    const h = first && first.style.height ? first.style.height : `${DAY_W - 4}px`
    const runs = []
    days.forEach((d) => { if (runs.length && runs[runs.length - 1][1] + 1 === d) runs[runs.length - 1][1] = d; else runs.push([d, d]) })
    runs.forEach(([a, b], i) => {
      const g = document.createElement("div")
      g.className = "cs-bar cs-ghost absolute rounded-[3px] text-[13px] font-medium overflow-hidden whitespace-nowrap text-ellipsis box-border px-1.5 pointer-events-none"
      g.style.cssText = `left:${this.pct(a)}px;width:${Math.max(4, this.pct(b + 1) - this.pct(a))}px;top:${first && first.style.top ? first.style.top : "2px"};height:${h};line-height:${h};background:${t.color || "#999"};color:${t.text || "#fff"};opacity:.8;box-shadow:0 4px 10px rgba(0,0,0,.25)`
      if (i === 0 && b - a + 1 >= 3) g.textContent = m.block.area || ""
      m.track.appendChild(g)
      m.ghosts.push(g)
    })
  }

  _clearGhosts(m) { (m.ghosts || []).forEach((g) => g.remove()); m.ghosts = [] }

  _moveDelta(m) {
    const len = m.e - m.s
    const raw = m.cur - m.anchor
    return Math.max(-m.s, Math.min(this.range - m.e, raw)) // keep the whole block inside the range
  }

  _paintMove(e) {
    const m = this._move
    if (m) {
      const day = this._dayFromEvent(e, m.track)
      if (day === m.cur) return
      m.cur = day
      const delta = this._moveDelta(m)
      this.boardTarget.classList.toggle("cs-moving", delta !== 0)
      if (this.state.skipOff && m.working && delta !== 0) {
        // Re-flow the preview around weekends and holidays: exactly where the drop will land.
        this._showGhosts(m, this._workDays(Math.max(0, m.s + delta), m.working))
        return
      }
      this._clearGhosts(m)
      m.bars.forEach((bar) => { bar.el.style.visibility = ""; bar.el.style.left = `${(bar.s + delta) * DAY_W}px`; bar.el.style.opacity = delta ? "0.75" : ""; bar.el.style.boxShadow = delta ? "0 4px 10px rgba(0,0,0,.25)" : "" })
      return
    }
    const p = this._paint
    if (!p) return
    const day = this._dayFromEvent(e, p.track)
    if (day === p.cur) return
    p.cur = day
    this._previewPaint()
  }

  _previewPaint() {
    const p = this._paint
    if (!p) return
    const lo = Math.min(p.anchor, p.cur), hi = Math.max(p.anchor, p.cur)
    const skip = this.state.skipOff && lo !== hi
    p.track.querySelectorAll("[data-cell]").forEach((c) => { const d = Number(c.dataset.day); c.classList.toggle("cs-paint", d >= lo && d <= hi && !(skip && this._isOff(d))) })
  }

  async _paintUp() {
    const m = this._move
    if (m) {
      this._move = null
      this.boardTarget.classList.remove("cs-moving")
      this._clearGhosts(m)
      const delta = this._moveDelta(m)
      if (delta === 0) { m.bars.forEach((bar) => { bar.el.style.visibility = ""; bar.el.style.left = `${bar.s * DAY_W}px`; bar.el.style.opacity = ""; bar.el.style.boxShadow = "" }); return } // it was a click: the click opens the menu
      this._justPainted = true
      let res
      const working = []
      m.chain.forEach((b) => { for (let d = this._day(b.start_on); d <= this._day(b.end_on); d++) if (!this._isOff(d)) working.push(d) })
      if (this.state.skipOff && working.length) {
        // Same number of working days, starting on the first working day at or after the new start.
        const days = this._workDays(Math.max(0, m.s + delta), working.length)
        if (days.length !== working.length) { this.render(); return }
        res = await this._json(`${this.blocksUrlValue}/${m.block.id}/relocate`, "POST", { days: days.map((d) => this._iso(this._dateAt(d).getTime())), block_ids: m.chain.map((b) => b.id) })
      } else {
        const start = this._iso(this._dateAt(m.s + delta).getTime()), end = this._iso(this._dateAt(m.e + delta - 1).getTime())
        res = await this._json(`${this.blocksUrlValue}/${m.block.id}`, "PATCH", { start_on: start, end_on: end })
      }
      if (res && res.ok) {
        if (res.blocks) this.blocks = this.blocks.filter((b) => !(b.job_id === m.pid && b.trade_id === m.tid)).concat(res.blocks)
        else Object.assign(m.block, res.block)
        this._toast(this._t("block-moved")); this._track("Crew Block Moved", { trade_id: m.tid, days: delta })
      }
      this.render()
      return
    }
    const p = this._paint
    if (!p) return
    this._paint = null
    this._justPainted = true // the click that ends this gesture must not open a menu
    const lo = Math.min(p.anchor, p.cur), hi = Math.max(p.anchor, p.cur)
    const skip = this.state.skipOff && lo !== hi // a single click on a weekend or holiday is on purpose
    const days = []
    for (let d = lo; d <= hi; d++) if (!(skip && this._isOff(d))) days.push(d)
    if (!days.length) { this.render(); return }
    if (p.tid === null) {
      // Rows by area: the row knows the part of the job, not the trade — ask.
      const cell = p.track.querySelector(`[data-cell][data-day="${lo}"]`)
      if (cell) this._openPick(cell, p.pid, lo, null, { area: p.area, days })
      return
    }
    await this._paintDays(p.pid, p.tid, days, "add")
  }

  async _paintDays(pid, tid, dayIdxs, mode, area = null) {
    const body = { job_id: pid, trade_id: tid, mode, days: dayIdxs.map((d) => this._iso(this._dateAt(d).getTime())) }
    if (area !== null && area !== undefined) body.area = area
    const res = await this._json(this.paintUrlValue, "POST", body)
    if (!res || !res.ok) { this.render(); return }
    this.blocks = this.blocks.filter((b) => !(b.job_id === pid && b.trade_id === tid)).concat(res.blocks)
    if (res.area) this._takeAreas(pid, res)
    if (res.placed) this._toast(this._placedText(res))
    const proj = this.jobs.find((x) => x.id === pid)
    if (proj && mode === "add" && !(proj.rows || []).includes(tid)) proj.rows = (proj.rows || []).concat([tid])
    this._track("Crew Days Painted", { trade_id: tid, days: dayIdxs.length, mode })
    this.render()
  }

  boardClick(e) {
    if (this._justPainted) { this._justPainted = false; return }
    const add = e.target.closest("[data-add-trade]")
    if (add) { this._openAddTrade(Number(add.dataset.addTrade)); return }
    const remove = e.target.closest("[data-remove-row]")
    if (remove) { this._removeRow(Number(remove.dataset.jobId), Number(remove.dataset.removeRow)); return }
    const addArea = e.target.closest("[data-add-area]")
    if (addArea) { this._openAddArea(Number(addArea.dataset.addArea)); return }
    const jobRow = e.target.closest("[data-job-head]")
    if (jobRow && !e.target.closest("button, a, input, select, [data-job-handle]")) {
      this._toggleJob(Number(jobRow.dataset.jobHead)); return
    }
    const ren = e.target.closest("[data-rename-job]")
    if (ren) { this._openRenameJob(Number(ren.dataset.renameJob)); return }
    const link = e.target.closest("[data-link-job]")
    if (link) { this._openLinkJob(Number(link.dataset.linkJob)); return }
    const rmArea = e.target.closest("[data-remove-area]")
    if (rmArea) { this._removeArea(Number(rmArea.dataset.jobId), rmArea.dataset.removeArea); return }
    const cell = e.target.closest("[data-cell]")
    if (!cell || this.state.tab !== "jobs") return
    const newrow = cell.closest("[data-newrow]")
    if (newrow) { this._openPick(cell, Number(newrow.dataset.jobId), Number(cell.dataset.day), null, newrow.dataset.newArea ? { askArea: true } : {}); return }
    const track = cell.closest("[data-track]")
    if (!track) return
    const ctx = this._rowCtx(track), day = Number(cell.dataset.day)
    const block = ctx.blockAt(day)
    if (block) {
      // A single click opens the block's menu after a beat, so a double-click can take the day off instead.
      this.closePick()
      if (this._clickTimer) clearTimeout(this._clickTimer)
      this._clickTimer = setTimeout(() => { this._clickTimer = null; this._openPop(cell, block, day) }, 260)
    }
  }

  // Double-click a filled square: that day comes off (trim an end, or split a block in the middle).
  boardDblClick(e) {
    if (this._clickTimer) { clearTimeout(this._clickTimer); this._clickTimer = null }
    if (this.state.tab !== "jobs") return
    const cell = e.target.closest("[data-cell]"), track = cell && cell.closest("[data-track]")
    if (!track) return
    const ctx = this._rowCtx(track), day = Number(cell.dataset.day)
    const block = ctx.blockAt(day)
    if (!block) {
      // Empty square touching a block (Phelipe 2026-09-15): grow that block
      // into it, same trade and area, no picker. With the weekend switch on,
      // "touching" hops over the off days in between (Friday ↔ Monday).
      if (this.state.skipOff && this._isOff(day)) return // a shaded weekend or holiday square is not a "white" one
      const next = this._touchingBlock(ctx, day)
      if (!next) return
      if (e.preventDefault) e.preventDefault()
      this.closePop(); this.closePick(); this.hideTip()
      const area = ctx.tid === null ? ctx.area : (next.area || null)
      this._paintDays(ctx.pid, next.trade_id, [day], "add", area)
      this._track("Crew Block Grown By Double Click", { trade_id: next.trade_id })
      return
    }
    if (e.preventDefault) e.preventDefault()
    this.closePop(); this.closePick(); this.hideTip()
    this._paintDays(ctx.pid, block.trade_id, [day], "clear")
  }

  // A folded job keeps its title row but grows it a little and draws every
  // block as a thin line in the trade's colour, stacked where they overlap
  // (Phelipe 2026-09-15) — the whole job at a glance without opening it.
  _collapsedHeadHtml(p, blocks, labelHtml) {
    const byId = {}; this.trades.forEach((t) => { byId[t.id] = t })
    const items = blocks.map((b) => ({ id: b.id, s: this._day(b.start_on), e: this._day(b.end_on), t: byId[b.trade_id] || {}, area: b.area || "" }))
      .filter((it) => it.e >= 0 && it.s < this.range).sort((a, b) => a.s - b.s || a.e - b.e)
    const laneEnds = []
    items.forEach((it) => { let i = laneEnds.findIndex((end) => end < it.s); if (i < 0) { laneEnds.push(it.e); i = laneEnds.length - 1 } else laneEnds[i] = it.e; it.lane = i })
    // Thicker lines on a taller row (Phelipe 2026-09-15); each line carries
    // its block id so the board's hover card names the trade and area.
    const PITCH = 8, THICK = 5
    const h = Math.max(40, 18 + laneEnds.length * PITCH)
    const lines = items.map((it) => {
      const s = Math.max(0, it.s), e = Math.min(this.range - 1, it.e)
      const tip = [it.t.name, it.area].filter(Boolean).join(" · ")
      return `<div data-bar-id="${it.id}" class="absolute rounded-full cursor-default" title="${this._esc(tip)}" style="left:${s * DAY_W + 2}px;width:${(e - s + 1) * DAY_W - 4}px;top:${11 + it.lane * PITCH}px;height:${THICK}px;background:${it.t.color || "#9ca3af"}"></div>`
    }).join("")
    // The name already truncates and the icons already sit right; the count
    // just needs to hold its place.
    const label = labelHtml
      .replace("text-gray-400 whitespace-nowrap", "text-gray-400 whitespace-nowrap shrink-0")
    // A folded job still has to show its conflicts (Phelipe 2026-09-17: "when
    // all of them are collapsed the user wants to see the conflicts from that
    // view as well"). Same pink row and striped bands as an open one.
    const lit = (this.conflictList || []).filter((c) => c.pids.includes(p.id) && this.state.showConflicts &&
      (this.state.highlight === null || this.state.highlight === c.key))
    const bands = lit.map((c) => `<div class="cs-conflict absolute top-0 bottom-0 box-border pointer-events-none" style="left:${c.left}px;width:${c.width}px;background:repeating-linear-gradient(135deg, rgba(208,1,27,0.28) 0 5px, rgba(208,1,27,0.06) 5px 10px);border-left:2px solid #d0011b;border-right:2px solid #d0011b"></div>`).join("")
    // White cells behind the lines (Phelipe 2026-09-15); only the title box keeps the grey.
    return `<div data-job-head="${p.id}" title="${this._esc(this._t("expand-job"))}" class="flex border-b border-gray-200 text-[13px] cursor-pointer" style="min-width:${this.state.labelW + this.trackW}px;height:${h}px;background:${lit.length ? "var(--color-red-50, #fff5f5)" : "var(--color-white, #fff)"}">
      <div class="sticky left-0 z-[2] ${lit.length ? "bg-red-50" : "bg-gray-100"} border-r border-gray-200 box-border flex items-center gap-2 px-3 shrink-0 overflow-hidden" style="width:var(--cs-label-w)">${label}</div>
      <div class="relative shrink-0" style="width:${this.trackW}px;height:${h}px;background:${lit.length ? "var(--color-red-50, #fff5f5)" : "var(--color-white, #fff)"}">${this._trackExtras()}${lines}${bands}</div></div>`
  }

  // The block on the same row right before or right after this day, if any
  // (the one before wins when both sides are taken).
  _touchingBlock(ctx, day) {
    const hop = (d, step) => {
      let x = d + step
      while (x >= 0 && x < this.range && this.state.skipOff && this._isOff(x)) x += step
      return x >= 0 && x < this.range ? ctx.blockAt(x) : null
    }
    return hop(day, -1) || hop(day, 1)
  }

  boardContext(e) {
    if (this.state.tab !== "jobs") return
    const handle = e.target.closest("[data-row-handle]")
    if (handle) { e.preventDefault(); const row = handle.closest("[data-row]"); if (row && row.dataset.rowArea !== undefined) this._openAreaMenu(row, handle); else this._openRowMenu(row, handle); return }
    const cell = e.target.closest("[data-cell]")
    if (!cell) return
    e.preventDefault()
    const newrow = cell.closest("[data-newrow]")
    if (newrow) { this._openPick(cell, Number(newrow.dataset.jobId), Number(cell.dataset.day), null, newrow.dataset.newArea ? { askArea: true } : {}); return }
    const track = cell.closest("[data-track]")
    if (!track) return
    const ctx = this._rowCtx(track), day = Number(cell.dataset.day)
    const block = ctx.blockAt(day)
    if (block) this._openPop(cell, block, day)
    else if (ctx.tid === null) this._openPick(cell, ctx.pid, day, null, { area: ctx.area })
    else this._openPick(cell, ctx.pid, day, ctx.tid)
  }

  // The badge lights up every conflict on the grid; a chip lights up just that one.
  toggleConflicts() {
    this.state.showConflicts = !this.state.showConflicts
    this.state.highlight = null
    this.render()
    if (this.state.showConflicts && this.conflictList && this.conflictList.length) this._scrollToDay(this.conflictList[0].s)
  }

  chipClick(e) {
    const key = e.currentTarget.dataset.chipKey
    this.state.highlight = this.state.highlight === key ? null : key
    this.render()
    const c = (this.conflictList || []).find((x) => x.key === key)
    if (c && this.state.highlight) this._scrollToDay(c.s)
  }

  _scrollToDay(day) {
    if (!this.hasBoardTarget) return
    const left = Math.max(0, (day - 3) * DAY_W)
    const right = (day + 6) * DAY_W
    const view = this.boardTarget.clientWidth - this.state.labelW
    if (day * DAY_W < this.boardTarget.scrollLeft || right > this.boardTarget.scrollLeft + view) this.boardTarget.scrollLeft = left
  }

  async _addRow(projectId, tradeId) {
    const res = await this._json(this.rowsUrlValue, "POST", { job_id: projectId, trade_id: tradeId })
    if (res && res.ok) { const p = this.jobs.find((x) => x.id === projectId); if (p && !(p.rows || []).includes(tradeId)) p.rows = (p.rows || []).concat([tradeId]); this.render() }
    return res && res.ok
  }

  async _removeRow(projectId, tradeId) {
    const res = await this._json(this.rowsUrlValue, "DELETE", { job_id: projectId, trade_id: tradeId })
    if (res && res.ok) { const p = this.jobs.find((x) => x.id === projectId); if (p) p.rows = (p.rows || []).filter((id) => id !== tradeId); this.render() }
  }

  // ── Drag a row (by its handle) to change the order on that job ────

  rowDragStart(e) {
    const jobHandle = e.target.closest("[data-job-handle]")
    if (jobHandle) { this._jobDragStart(e, Number(jobHandle.dataset.jobHandle), jobHandle); return }
    const handle = e.target.closest("[data-row-handle]")
    const row = handle && handle.closest("[data-row]")
    if (!row || (!row.dataset.rowTid && !row.dataset.rowArea)) { e.preventDefault(); return }
    const tid = row.dataset.rowTid ? Number(row.dataset.rowTid) : null
    const area = row.dataset.rowTid ? null : row.dataset.rowArea
    const trade = tid !== null ? this.trades.find((t) => t.id === tid) : null
    this._rowDrag = { pid: Number(row.dataset.rowPid), tid, area, key: this._rowKey(row), height: row.offsetHeight || DAY_W, name: trade ? trade.name : (area || ""), row }
    row.classList.add("cs-row-dragging")
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = "move"
      try { e.dataTransfer.setData("text/plain", String(tid === null ? area : tid)) } catch { /* Safari */ }
      // The ghost under the cursor is the whole row, blocks included, not just the name.
      try { const r = handle.getBoundingClientRect(); e.dataTransfer.setDragImage(row, Math.max(0, e.clientX - r.left), Math.max(0, e.clientY - r.top)) } catch { /* jsdom, old browsers */ }
    }
    // Fold the row away once the drag has started, so the dashed slot is the only copy on screen.
    setTimeout(() => { if (this._rowDrag && this._rowDrag.row === row) row.classList.add("cs-row-collapsed") }, 0)
  }

  // Where can a row land? On another row of the same job (above or below it),
  // on the job's header (top) or on its "+ Add trade" line (bottom).
  _rowKey(row) { return row.dataset.rowTid ? `t${row.dataset.rowTid}` : `a${(row.dataset.rowArea || "").toLowerCase()}` }

  _dropSpot(e, d) {
    if (this._dropSlot && e.target.closest && e.target.closest(".cs-drop-slot") === this._dropSlot) return { slot: true }
    const row = e.target.closest("[data-row]")
    if (row) return Number(row.dataset.rowPid) === d.pid && this._rowKey(row) !== d.key ? { row, before: e.clientY < row.getBoundingClientRect().top + row.getBoundingClientRect().height / 2 } : (row.dataset.rowPid == d.pid ? { same: true } : null)
    const head = e.target.closest("[data-job-head]")
    if (head && Number(head.dataset.jobHead) === d.pid) return { head }
    const tail = e.target.closest("[data-job-tail]")
    if (tail && Number(tail.dataset.jobTail) === d.pid) return { tail }
    return null
  }

  rowDragOver(e) {
    if (this._jobDrag) { this._jobDragOver(e); return }
    const d = this._rowDrag
    if (!d) return
    const spot = this._dropSpot(e, d)
    if (!spot) return
    e.preventDefault()
    if (e.dataTransfer) e.dataTransfer.dropEffect = "move"
    if (spot.same) { this._clearDropMarks(); return }
    if (spot.slot) return // the slot already sits where the row will land
    const width = `${this.state.labelW + this.trackW}px`
    if (spot.row) this._placeSlot(spot.row, spot.before, d.height, width, d.name)
    else if (spot.tail) this._placeSlot(spot.tail, true, d.height, width, d.name)
    else if (spot.head) {
      let first = spot.head.nextElementSibling
      while (first && (first === this._dropSlot || first.classList.contains("cs-row-collapsed"))) first = first.nextElementSibling
      if (first) this._placeSlot(first, true, d.height, width, d.name)
    }
  }

  // A dashed slot the size of the dragged row, right where it will land.
  _placeSlot(anchor, before, height, minWidth, name) {
    const slot = this._dropSlot || (this._dropSlot = document.createElement("div"))
    slot.className = "cs-drop-slot"
    slot.style.height = `${height}px`
    slot.style.minWidth = minWidth
    slot.style.pointerEvents = ""
    slot.textContent = name || ""
    slot.title = name || ""
    const wanted = before ? anchor : anchor.nextSibling
    if (slot.parentNode !== anchor.parentNode || slot.nextSibling !== wanted) anchor.parentNode.insertBefore(slot, wanted)
  }

  async rowDrop(e) {
    if (this._jobDrag) { await this._jobDrop(e); return }
    const d = this._rowDrag
    if (!d) return
    const spot = this._dropSpot(e, d)
    // Let go on the slot: it lands right there — read the row the slot sits above before the slot goes away.
    let slotNextTid = null, slotNextArea = null, slotAtEnd = false
    if (spot && spot.slot && this._dropSlot) {
      let next = this._dropSlot.nextElementSibling
      while (next && next.classList.contains("cs-row-collapsed")) next = next.nextElementSibling
      if (next && next.dataset.row) { slotNextTid = next.dataset.rowTid ? Number(next.dataset.rowTid) : null; slotNextArea = next.dataset.rowArea === undefined ? null : next.dataset.rowArea } else slotAtEnd = true
    }
    this.rowDragEnd()
    if (!spot || spot.same) return
    e.preventDefault()
    const p = this.jobs.find((x) => x.id === d.pid)
    if (!p) return
    if (d.area !== null) {
      // Area rows: the names in their new order, saved as the job's area order.
      const mine = this.blocks.filter((b) => b.job_id === p.id)
      const same = (a, b) => a.toLowerCase() === b.toLowerCase()
      const without = this._areaOrder(p.id, mine).filter((k) => k !== "" && !same(k, d.area))
      if (spot.slot) { const at = slotNextArea === null ? -1 : without.findIndex((k) => same(k, slotNextArea)); if (slotAtEnd || at === -1) without.push(d.area); else without.splice(at, 0, d.area) }
      else if (spot.row) { const at = without.findIndex((k) => same(k, spot.row.dataset.rowArea || "")); if (at === -1) without.push(d.area); else without.splice(spot.before ? at : at + 1, 0, d.area) }
      else if (spot.head) without.unshift(d.area)
      else without.push(d.area)
      without.forEach((k, i) => { const a = this.areas.find((x) => x.job_id === p.id && same(x.name, k)); if (a) a.position = i; else this.areas.push({ id: null, job_id: p.id, name: k, position: i }) })
      this.render()
      const res = await this._json(this.areasUrlValue, "PATCH", { job_id: p.id, names: without })
      if (res && res.ok && res.areas) { this.areas = this.areas.filter((a) => a.job_id !== p.id).concat(res.areas); this.render() }
      return
    }
    const order = (p.rows || []).slice()
    this.trades.forEach((t) => { if (!order.includes(t.id) && this.blocks.some((b) => b.job_id === p.id && b.trade_id === t.id)) order.push(t.id) })
    const without = order.filter((id) => id !== d.tid)
    if (spot.slot) { const at = slotNextTid === null ? -1 : without.indexOf(slotNextTid); if (slotAtEnd || at === -1) without.push(d.tid); else without.splice(at, 0, d.tid) }
    else if (spot.row) { const at = without.indexOf(Number(spot.row.dataset.rowTid)); without.splice(spot.before ? at : at + 1, 0, d.tid) }
    else if (spot.head) without.unshift(d.tid)
    else without.push(d.tid)
    p.rows = without
    this.render()
    const res = await this._json(this.rowsUrlValue, "PATCH", { job_id: p.id, trade_ids: without })
    if (res && res.ok && res.rows) { p.rows = res.rows; this.render() }
  }

  rowDragEnd() { this._rowDrag = null; this._jobDrag = null; this._clearDropMarks(); this._clearJobMarks(); this.boardTarget.querySelectorAll(".cs-row-dragging, .cs-row-collapsed").forEach((el) => el.classList.remove("cs-row-dragging", "cs-row-collapsed")) }

  // ── Drag a whole job (its grip) above or below another job (Phelipe 2026-09-16) ──
  // The order is the company's, like the rows': it is saved on the jobs.

  _orderedJobs(list) {
    return (list || []).slice().sort((a, b) => ((a.position ?? 1e9) - (b.position ?? 1e9)) || String(a.name || "").localeCompare(String(b.name || "")))
  }

  // Everything the board draws for one job: its header down to the line before the next header.
  _jobElements(pid) {
    const head = this.boardTarget.querySelector(`[data-job-head="${pid}"]`)
    const els = []
    for (let el = head; el; el = el.nextElementSibling) { if (el !== head && el.dataset.jobHead !== undefined) break; els.push(el) }
    return els
  }

  // Which job the pointer is over: walk up to the board's own child, then back to the nearest header.
  _jobAt(target) {
    let el = target
    while (el && el.parentNode !== this.boardTarget) el = el.parentNode
    for (; el; el = el.previousElementSibling) if (el.dataset && el.dataset.jobHead !== undefined) return Number(el.dataset.jobHead)
    return null
  }

  _jobDragStart(e, pid, handle) {
    this._jobDrag = { pid }
    const head = this.boardTarget.querySelector(`[data-job-head="${pid}"]`)
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = "move"
      try { e.dataTransfer.setData("text/plain", String(pid)) } catch { /* Safari */ }
      try { const r = handle.getBoundingClientRect(); if (head) e.dataTransfer.setDragImage(head, Math.max(0, e.clientX - r.left), Math.max(0, e.clientY - r.top)) } catch { /* jsdom, old browsers */ }
    }
    this._jobElements(pid).forEach((el) => el.classList.add("cs-row-dragging"))
  }

  _jobDragOver(e) {
    const d = this._jobDrag, pid = this._jobAt(e.target)
    if (pid === null) return
    e.preventDefault()
    if (e.dataTransfer) e.dataTransfer.dropEffect = "move"
    if (pid === d.pid) { this._clearJobMarks(); return }
    const order = this._orderedJobs(this.jobs).map((j) => j.id)
    const before = order.indexOf(d.pid) > order.indexOf(pid)
    const els = this._jobElements(pid)
    if (!els.length) return
    const mark = before ? els[0] : els[els.length - 1], cls = before ? "cs-job-drop-before" : "cs-job-drop-after"
    if (!mark.classList.contains(cls)) { this._clearJobMarks(); mark.classList.add(cls) }
    const moving = this.jobs.find((j) => j.id === d.pid)
    this._placeJobSlot(mark, before, moving ? moving.name : "")
  }

  _clearJobMarks() {
    this.boardTarget.querySelectorAll(".cs-job-drop-before, .cs-job-drop-after").forEach((el) => el.classList.remove("cs-job-drop-before", "cs-job-drop-after"))
    if (this._dropSlot && this._dropSlot.parentNode) this._dropSlot.parentNode.removeChild(this._dropSlot)
  }

  // A dashed gap carrying the job's name, showing where it will land
  // (Phelipe 2026-09-17: "when drag and dropping an entire row show where it
  // fits"). It ignores the pointer, so the job underneath stays the drop
  // target that _jobDrop reads.
  _placeJobSlot(anchor, before, name) {
    const slot = this._dropSlot || (this._dropSlot = document.createElement("div"))
    slot.className = "cs-drop-slot"
    slot.style.height = "26px"
    slot.style.minWidth = `${this.state.labelW}px`
    slot.style.pointerEvents = "none"
    slot.textContent = name || ""
    slot.title = ""
    const wanted = before ? anchor : anchor.nextSibling
    if (slot.parentNode !== anchor.parentNode || slot.nextSibling !== wanted) anchor.parentNode.insertBefore(slot, wanted)
  }

  async _jobDrop(e) {
    const d = this._jobDrag, pid = this._jobAt(e.target)
    this.rowDragEnd()
    if (pid === null || pid === d.pid) return
    e.preventDefault()
    const was = this._orderedJobs(this.jobs).map((j) => j.id)
    const before = was.indexOf(d.pid) > was.indexOf(pid)
    const order = was.filter((id) => id !== d.pid)
    const at = order.indexOf(pid)
    order.splice(before ? at : at + 1, 0, d.pid)
    order.forEach((id, i) => { const j = this.jobs.find((x) => x.id === id); if (j) j.position = i + 1 })
    this.render()
    const res = await this._json(this.reorderJobsUrlValue, "PATCH", { job_ids: order })
    if (res && res.ok && res.positions) { Object.entries(res.positions).forEach(([id, pos]) => { const j = this.jobs.find((x) => x.id === Number(id)); if (j) j.position = pos }); this.render() }
    this._track("Crew Jobs Reordered", { jobs: order.length })
  }

  _clearDropMarks() {
    this.boardTarget.querySelectorAll(".cs-drop-before, .cs-drop-after").forEach((el) => el.classList.remove("cs-drop-before", "cs-drop-after"))
    if (this._dropSlot && this._dropSlot.parentNode) this._dropSlot.parentNode.removeChild(this._dropSlot)
  }

  // ── + Add trade: a pop-up with the trades not yet on the job ──────

  _openAddTrade(pid) {
    const p = this.jobs.find((x) => x.id === pid)
    if (!p) return
    this._addingTo = pid
    const onJob = new Set(this.blocks.filter((b) => b.job_id === pid).map((b) => b.trade_id).concat(p.rows || []))
    const items = this.trades.filter((t) => !onJob.has(t.id))
    this.addTradeTitleTarget.textContent = p.name
    this.addTradeListTarget.innerHTML = items.length
      ? items.map((t) => `<button type="button" data-add-trade-pick="${t.id}" data-action="click->crew-schedule#addTradeFromModal" class="w-full flex items-center gap-3 px-2 py-2.5 text-[13px] text-gray-800 hover:bg-gray-50 rounded-lg cursor-pointer text-left"><span class="w-4 h-4 rounded-[3px] shrink-0" style="background:${t.color}"></span><span class="flex-1">${this._esc(t.name)}</span>${t.sub ? '<span class="text-gray-400">sub</span>' : (t.crews > 1 ? `<span class="text-gray-400">×${t.crews}</span>` : "")}</button>`).join("")
      : `<div class="py-6 text-center text-[13px] text-gray-400 italic">${this._esc(this._t("every-trade"))}</div>`
    this.addTradeModalTarget.classList.remove("hidden")
  }

  closeAddTrade() { if (this.hasAddTradeModalTarget) this.addTradeModalTarget.classList.add("hidden"); this._addingTo = null }

  async addTradeFromModal(e) {
    const pid = this._addingTo, tid = Number(e.currentTarget.dataset.addTradePick)
    this.closeAddTrade()
    if (!pid) return
    await this._addRow(pid, tid)
  }

  openManageFromAdd() { this.closeAddTrade(); this.openManage() }

  // ── Pick a trade for a square ─────────────────────────────────────

  _openPick(cell, pid, day, currentTid, opts = {}) {
    this.closePop()
    const days = opts.days && opts.days.length ? opts.days : [day]
    this._pickAt = { pid, day, tid: currentTid, area: opts.area === undefined ? null : opts.area, days, askArea: !!opts.askArea }
    if (this.hasPickAreaWrapTarget) { this.pickAreaWrapTarget.classList.toggle("hidden", !opts.askArea); if (opts.askArea) this.pickAreaTarget.value = "" }
    if (this.hasPickNoteWrapTarget) {
      this.pickNoteWrapTarget.classList.toggle("hidden", !currentTid)
      if (currentTid) { const n = this._noteAt(pid, currentTid, day); this.pickNoteTarget.value = n ? n.body : ""; this.pickNoteDayTarget.textContent = this._fmt(day) }
    }
    const p = this.jobs.find((x) => x.id === pid)
    const last = days[days.length - 1]
    this.pickTitleTarget.textContent = `${p ? p.name : ""}${opts.area ? " · " + opts.area : ""} · ${this._fmt(day)}${last !== day ? " – " + this._fmt(last) : ""}`
    const items = this.trades.filter((t) => t.id !== currentTid)
    this.pickListTarget.innerHTML = items.map((t) => `<button type="button" data-pick-trade="${t.id}" data-action="click->crew-schedule#pickTrade" class="inline-flex items-center gap-1.5 h-[26px] px-2.5 rounded-lg border border-gray-300 bg-white text-gray-700 text-[13px] cursor-pointer whitespace-nowrap hover:bg-gray-50"><span class="w-3 h-3 rounded-[3px]" style="background:${t.color}"></span>${this._esc(t.name)}</button>`).join("")
    this._place(this.pickTarget, cell)
  }

  closePick() { if (this.hasPickTarget) this.pickTarget.classList.add("hidden"); this._pickAt = null }

  async pickTrade(e) {
    const at = this._pickAt
    const tid = Number(e.currentTarget.dataset.pickTrade)
    const area = at ? (at.askArea && this.hasPickAreaTarget ? this.pickAreaTarget.value.trim() : at.area) : null
    this.closePick()
    if (!at) return
    await this._paintDays(at.pid, tid, at.days, "add", area)
  }

  // ── Row menu (right-click a row name): switch the trade, remove the row ──

  _openRowMenu(row, anchorEl) {
    if (!row) return
    this.closePop(); this.closePick()
    const pid = Number(row.dataset.rowPid), tid = Number(row.dataset.rowTid)
    const p = this.jobs.find((x) => x.id === pid), t = this.trades.find((x) => x.id === tid)
    if (!p || !t) return
    const count = this.blocks.filter((b) => b.job_id === pid && b.trade_id === tid).length
    this._rowMenuFor = { pid, tid, count }
    this.rowMenuTitleTarget.textContent = `${p.name} · ${t.name}`
    this.rowMenuMetaTarget.textContent = count ? this._statsText(this._rowStats(pid, tid)) : "empty row"
    this.rowMenuTradesTarget.innerHTML = this.trades.filter((x) => x.id !== tid).map((x) => `<button type="button" data-switch-trade="${x.id}" data-action="click->crew-schedule#rowMenuSwitch" class="inline-flex items-center gap-1.5 h-[24px] px-2 rounded-lg border border-gray-300 bg-white text-gray-700 text-[13px] cursor-pointer whitespace-nowrap hover:bg-gray-50"><span class="w-3 h-3 rounded-[3px]" style="background:${x.color}"></span>${this._esc(x.name)}</button>`).join("")
    this.rowMenuRemoveTarget.textContent = count ? this._t("remove-row-blocks") : this._t("remove-row")
    this._place(this.rowMenuTarget, anchorEl)
  }

  closeRowMenu() { if (this.hasRowMenuTarget) this.rowMenuTarget.classList.add("hidden"); this._rowMenuFor = null }
  rowMenuEditTrade() { if (this._rowMenuFor) this._openEditTrade(this._rowMenuFor.tid, this._rowMenuFor.pid) }
  popEditTrade() { if (this._menuBlock) this._openEditTrade(this._menuBlock.trade_id, this._menuBlock.job_id) }

  // Enter anywhere in the block menu saves; Shift+Enter still makes a new line in the note.
  popKey(e) {
    if (e.key !== "Enter" || e.shiftKey) return
    e.preventDefault()
    this.popSave()
  }

  // Save: whatever changed in the block menu (note, crews here, crews you have, people per crew), then close it.
  async popSave() {
    const block = this._menuBlock
    if (!block) return
    const t = this.trades.find((x) => x.id === block.trade_id)
    let did = false
    if (this.hasPopNoteTarget) {
      const cur = this._noteAt(block.job_id, block.trade_id, this._menuDay)
      if (this.popNoteTarget.value.trim() !== (cur ? cur.body : "")) { await this.saveNote({ currentTarget: this.popNoteTarget }); did = true }
    }
    if (this.hasPopCrewsTarget && Number(this.popCrewsTarget.value) !== (Number(block.crews) || 1)) { await this.popSaveCrews({ currentTarget: this.popCrewsTarget }); did = true }
    if (t && this.hasPopHaveTarget && Number(this.popHaveTarget.value) !== Number(t.crews)) { await this.popSaveTrade({ currentTarget: this.popHaveTarget }); did = true }
    if (t && this.hasPopPeopleTarget && String(this.popPeopleTarget.value) !== String(t.people ?? "")) { await this.popSaveTrade({ currentTarget: this.popPeopleTarget }); did = true }
    this.closePop()
    if (!did) this._toast(this._t("saved"))
  }

  // Crews you have and people per crew, edited right in the block menu (they belong to the trade).
  async popSaveTrade(e) {
    const block = this._menuBlock
    if (!block) return
    const el = e.currentTarget
    const res = await this._json(`${this.tradesUrlValue}/${block.trade_id}`, "PATCH", { trade: { [el.dataset.field]: el.value } })
    if (!res || !res.ok) return
    const i = this.trades.findIndex((t) => t.id === block.trade_id)
    if (i > -1) this.trades[i] = res.trade
    this._popPeopleHere()
    this._toast(this._t("saved")); this._track("Crew Trade Updated", { trade_id: block.trade_id }); this.render()
  }

  // How many crews this block takes: feeds the conflict check and the ×N on the bar.
  async popSaveCrews(e) {
    const block = this._menuBlock
    if (!block) return
    const res = await this._json(`${this.blocksUrlValue}/${block.id}`, "PATCH", { crews: e.currentTarget.value })
    if (!res || !res.ok) return
    Object.assign(block, res.block)
    this.popCrewsTarget.value = block.crews || 1
    this._popPeopleHere()
    this._toast(this._t("saved")); this._track("Crew Block Crews Set", { trade_id: block.trade_id, crews: block.crews }); this.render()
  }

  _popPeopleHere() {
    if (!this.hasPopPeopleHereTarget) return
    const per = Number(this.popPeopleTarget.value) || 0, here = Number(this.popCrewsTarget.value) || 1
    this.popPeopleHereTarget.textContent = per ? `= ${here * per} ${this._t("pop-people-here")}` : ""
  }

  // ── Sharing: read-only link, look-ahead email ─────────────────────

  async copyShareLink(e) {
    this._closeDropdown(e)
    const res = await this._json(this.shareUrlValue, "POST", {})
    if (res && res.ok) this._copyLink(res.url, this._t("link-copied"))
  }

  async resetShareLink(e) {
    this._closeDropdown(e)
    const res = await this._json(this.shareUrlValue, "POST", { reset: 1 })
    if (res && res.ok) this._copyLink(res.url, this._t("link-reset"))
  }

  async _copyLink(url, msg) {
    try { if (navigator.clipboard && navigator.clipboard.writeText) { await navigator.clipboard.writeText(url); this._toast(msg); return } } catch { /* fall back to the prompt */ }
    this._lastShareUrl = url
    if (typeof window.prompt === "function") window.prompt(msg, url)
  }

  _closeDropdown(e) { const m = e && e.currentTarget && e.currentTarget.closest('[data-dropdown-target="menu"]'); if (m) m.classList.add("hidden") }

  openLookahead(e) { this._closeDropdown(e); if (this.hasLookaheadModalTarget) this.lookaheadModalTarget.classList.remove("hidden") }
  closeLookahead() { if (this.hasLookaheadModalTarget) this.lookaheadModalTarget.classList.add("hidden") }

  async sendLookahead(e) {
    if (e && e.preventDefault) e.preventDefault()
    const f = (n) => this.lookaheadFormTarget.querySelector(`[name="${n}"]`)
    const btn = this.lookaheadFormTarget.querySelector('button[type="submit"]')
    if (btn) btn.disabled = true
    const res = await this._json(this.lookaheadUrlValue, "POST", { emails: f("emails").value, weeks: f("weeks").value, weekly: f("weekly").checked ? "1" : "0" })
    if (btn) btn.disabled = false
    if (!res || !res.ok) return
    this.closeLookahead()
    this._toast(`${this._t("lookahead-sent")} · ${res.sent}`)
    this._track("Crew Lookahead Sent", { recipients: res.sent, weekly: res.weekly })
  }

  // ── Edit one trade (from a row or a bar) ──────────────────────────

  _openEditTrade(tid, pid) {
    const t = this.trades.find((x) => x.id === tid)
    if (!t || !this.hasEditTradeModalTarget) return
    this.closePop(); this.closeRowMenu(); this.hideTip()
    this._editTradeId = tid
    const f = (n) => this.editTradeFormTarget.querySelector(`[name="${n}"]`)
    f("name").value = t.name; f("color").value = t.color; f("crews_count").value = t.crews || 1; f("people_per_crew").value = t.people ?? ""; f("sub").checked = !!t.sub
    const p = pid ? this.jobs.find((x) => x.id === pid) : null
    const st = p ? this._statsText(this._rowStats(pid, tid)) : ""
    this.editTradeMetaTarget.textContent = p ? `${t.name} · ${p.name}${st ? " · " + st : ""}` : t.name
    this.editTradeModalTarget.classList.remove("hidden")
    setTimeout(() => f("name").focus(), 0)
  }

  closeEditTrade() { if (this.hasEditTradeModalTarget) this.editTradeModalTarget.classList.add("hidden"); this._editTradeId = null }

  async saveEditTrade(e) {
    if (e && e.preventDefault) e.preventDefault()
    const id = this._editTradeId
    if (!id) return
    const f = (n) => this.editTradeFormTarget.querySelector(`[name="${n}"]`)
    const trade = { name: f("name").value.trim(), color: f("color").value, crews_count: f("crews_count").value, people_per_crew: f("people_per_crew").value, sub: f("sub").checked ? "1" : "0" }
    if (!trade.name) return
    const res = await this._json(`${this.tradesUrlValue}/${id}`, "PATCH", { trade })
    if (!res || !res.ok) return
    const i = this.trades.findIndex((t) => t.id === id)
    if (i > -1) this.trades[i] = res.trade
    this.closeEditTrade(); this._toast(this._t("saved")); this._track("Crew Trade Updated", { trade_id: id }); this.render()
  }

  async rowMenuSwitch(e) {
    const m = this._rowMenuFor, to = Number(e.currentTarget.dataset.switchTrade)
    this.closeRowMenu()
    if (!m) return
    const res = await this._json(`${this.rowsUrlValue}/switch`, "PATCH", { job_id: m.pid, from_trade_id: m.tid, to_trade_id: to })
    if (!res || !res.ok) return
    this.blocks = this.blocks.filter((b) => !(b.job_id === m.pid && (b.trade_id === m.tid || b.trade_id === to))).concat(res.blocks || [])
    const p = this.jobs.find((x) => x.id === m.pid)
    if (p) { p.rows = (p.rows || []).filter((id) => id !== m.tid); if (!p.rows.includes(to)) p.rows = p.rows.concat([to]) }
    this._toast(this._t("row-switched")); this.render()
  }

  rowMenuRemove() {
    const m = this._rowMenuFor
    this.closeRowMenu()
    if (!m) return
    if (!m.count) { this._removeRow(m.pid, m.tid); return }
    const p = this.jobs.find((x) => x.id === m.pid), t = this.trades.find((x) => x.id === m.tid)
    this._pendingRowRemoval = m
    this.deleteTitleTarget.textContent = this._t("remove-row-title")
    this.deleteTextTarget.textContent = `${p ? p.name : ""} · ${t ? t.name : ""} · ${m.count} ${m.count === 1 ? "block" : "blocks"}`
    this.deleteModalTarget.classList.remove("hidden")
  }

  // ── Block menu (click or right-click a filled square) ─────────────

  _openPop(cell, block, day) {
    this.closePick()
    this._menuBlock = block
    this._menuDay = day
    const p = this.jobs.find((x) => x.id === block.job_id), t = this.trades.find((x) => x.id === block.trade_id)
    this.popTitleTarget.textContent = `${p ? p.name : ""} · ${t ? t.name : ""}`
    const s = this._day(block.start_on), en = this._day(block.end_on) + 1
    this.popDatesTarget.textContent = en - s === 1 ? `${this._fmt(s)} · 1 ${this._t("tip-day")}` : `${this._fmt(s)} – ${this._fmtEnd(en)} · ${en - s} ${this._t("tip-days")} ${this._t("tip-in-row")}`
    if (this.hasPopMetaTarget) this.popMetaTarget.textContent = this._statsText(this._rowStats(block.job_id, block.trade_id), false) || "—"
    if (this.hasPopEditLinkTarget) this.popEditLinkTarget.classList.toggle("hidden", this.state.rowsBy !== "area")
    if (this.hasPopCrewsTarget) { this.popCrewsTarget.value = block.crews || 1; this.popHaveTarget.value = (t && t.crews) || 1; this.popPeopleTarget.value = t && t.people != null ? t.people : ""; this._popPeopleHere() }
    if (this.hasPopNoteTarget) { const n = this._noteAt(block.job_id, block.trade_id, day); this.popNoteTarget.value = n ? n.body : ""; this.popNoteDayTarget.textContent = this._fmt(day) }
    this._place(this.popTarget, cell)
  }

  closePop() { if (this.hasPopTarget) this.popTarget.classList.add("hidden"); this._menuBlock = null }

  _place(panel, anchorEl) {
    const host = this.element.getBoundingClientRect(), r = anchorEl.getBoundingClientRect()
    panel.classList.remove("hidden")
    const w = panel.offsetWidth || 288
    panel.style.left = `${Math.max(8, Math.min(r.left - host.left, host.width - w - 8))}px`
    panel.style.top = `${r.bottom - host.top + 6}px`
  }

  // The note on one day of a row, from the block menu or the empty-square picker; empty = take it off.
  async saveNote(e) {
    const fromPop = this.hasPopNoteTarget && e.currentTarget === this.popNoteTarget
    const ctx = fromPop ? (this._menuBlock ? { pid: this._menuBlock.job_id, tid: this._menuBlock.trade_id, day: this._menuDay } : null)
      : (this._pickAt && this._pickAt.tid ? { pid: this._pickAt.pid, tid: this._pickAt.tid, day: this._pickAt.day } : null)
    if (!ctx) return
    const body = e.currentTarget.value.trim()
    const on = this._iso(this._dateAt(ctx.day).getTime())
    const res = await this._json(this.notesUrlValue, "POST", { job_id: ctx.pid, trade_id: ctx.tid, on, body })
    if (!res || !res.ok) return
    this.notes = (this.notes || []).filter((n) => !(n.job_id === ctx.pid && n.trade_id === ctx.tid && n.on === on))
    if (res.note) this.notes.push(res.note)
    const proj = this.jobs.find((x) => x.id === ctx.pid)
    if (proj && res.note && !(proj.rows || []).includes(ctx.tid)) proj.rows = (proj.rows || []).concat([ctx.tid])
    this._toast(this._t(res.note ? "note-saved" : "note-removed")); this._track("Crew Note Saved", { trade_id: ctx.tid, removed: !res.note })
    this.render()
  }

  async removeDay() {
    const block = this._menuBlock, day = this._menuDay
    this.closePop()
    if (!block) return
    await this._paintDays(block.job_id, block.trade_id, [day], "clear")
  }

  askRemove() {
    const block = this._menuBlock
    if (!block) return
    this._pendingRowRemoval = null
    if (this.hasDeleteTitleTarget) this.deleteTitleTarget.textContent = this._t("remove-block-title")
    this.deleteTextTarget.textContent = `${this.popTitleTarget.textContent} · ${this.popDatesTarget.textContent}`
    this.deleteModalTarget.classList.remove("hidden")
  }

  hideDelete() { if (this.hasDeleteModalTarget) this.deleteModalTarget.classList.add("hidden"); this._pendingRowRemoval = null }
  stopPropagation(e) { e.stopPropagation() }

  async confirmRemove() {
    const pending = this._pendingRowRemoval
    if (pending) {
      this._pendingRowRemoval = null
      this.hideDelete()
      const res = await this._json(this.rowsUrlValue, "DELETE", { job_id: pending.pid, trade_id: pending.tid, force: 1 })
      if (res && res.ok) {
        this.blocks = this.blocks.filter((b) => !(b.job_id === pending.pid && b.trade_id === pending.tid))
        const p = this.jobs.find((x) => x.id === pending.pid); if (p) p.rows = (p.rows || []).filter((id) => id !== pending.tid)
        this._toast(this._t("row-removed")); this.render()
      }
      return
    }
    const block = this._menuBlock
    this.hideDelete()
    if (!block) return
    const res = await this._json(`${this.blocksUrlValue}/${block.id}`, "DELETE")
    if (res && res.ok) { this.blocks = this.blocks.filter((b) => b.id !== block.id); this.closePop(); this._toast(this._t("block-removed")); this._track("Crew Block Deleted", { trade_id: block.trade_id }); this.render() }
  }

  // ── Smart import: file → preview → import ─────────────────────────

  openImport() {
    this._importFile = null; this._importPlan = null
    this.importFileTarget.value = ""
    this.importFileNameTarget.textContent = this.importDropTarget.dataset.emptyLabel || (this.importDropTarget.dataset.emptyLabel = this.importFileNameTarget.textContent)
    this._importStatus(null)
    this.importErrorTarget.classList.add("hidden")
    if (this.hasImportModeTarget) { this.importModeTargets.forEach((r) => { r.checked = r.value === "add" }); this.importModeChanged() }
    this._importStep(1)
    this.importModalTarget.classList.remove("hidden")
  }

  // The one choice on the first step: add on top of what is there (default), or replace everything.
  _importChoice() { const r = this.hasImportModeTarget ? this.importModeTargets.find((x) => x.checked) : null; return r ? r.value : "add" }
  _importMode() { return this._importChoice() === "replace_all" ? "replace_all" : "add" }
  importModeChanged() { if (this.hasImportModeWarnTarget) this.importModeWarnTarget.classList.toggle("hidden", this._importChoice() !== "replace_all") }

  closeImport() { if (this.hasImportModalTarget) this.importModalTarget.classList.add("hidden") }

  importPick() { this.importFileTarget.click() }
  importFileChosen() { const f = this.importFileTarget.files && this.importFileTarget.files[0]; if (f) this._importTake(f) }
  importDragOver(e) { e.preventDefault(); this.importDropTarget.classList.add("border-purple-400", "bg-purple-50") }
  importDragLeave() { this.importDropTarget.classList.remove("border-purple-400", "bg-purple-50") }
  importDrop(e) {
    e.preventDefault(); this.importDragLeave()
    const f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0]
    if (f) this._importTake(f)
  }

  _importTake(file) {
    this._importFile = file
    this.importFileNameTarget.textContent = file.name
    this.importErrorTarget.classList.add("hidden")
    this.importRead() // a dropped file is read straight away
  }

  _importStep(n) {
    this.importStep1Target.classList.toggle("hidden", n !== 1)
    this.importStep2Target.classList.toggle("hidden", n !== 2)
    this.importBackTarget.classList.toggle("hidden", n !== 2)
    this.importApplyBtnTarget.classList.toggle("hidden", n !== 2)
  }

  importBackToFile() { this._importStep(1) }

  async importRead() {
    if (!this._importFile) return
    const fd = new FormData()
    fd.append("file", this._importFile)
    fd.append("year", this.importYearTarget.value)
    if (this._importReading) return
    this._importReading = true
    this._importStatus(`${this._t("import-reading")} ${this._importFile.name}`)
    this.importErrorTarget.classList.add("hidden")
    try {
      const res = await fetch(this.importPreviewUrlValue, { method: "POST", headers: { Accept: "application/json", "X-CSRF-Token": this.csrfValue }, body: fd })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.ok) { this._importFail(data.error || this._t("error")); return }
      this._importPlan = data
      this._renderImportPlan(data)
      this._importStep(2)
    } catch { this._importFail(this._t("error")) }
    finally { this._importReading = false; this._importStatus(null) }
  }

  _importStatus(text) {
    if (!this.hasImportStatusTarget) return
    this.importStatusTarget.classList.toggle("hidden", !text)
    if (this.hasImportStatusTextTarget) this.importStatusTextTarget.textContent = text || ""
  }

  _importFail(message) { this.importErrorTarget.textContent = message; this.importErrorTarget.classList.remove("hidden") }

  _renderImportPlan(plan) {
    const warnings = plan.warnings || []
    this.importWarningsTarget.textContent = warnings.join(" ")
    this.importWarningsTarget.classList.toggle("hidden", warnings.length === 0)
    this.importTradesTarget.innerHTML = (plan.trades || []).map((t) => `<span class="inline-flex items-center gap-1.5 h-[26px] px-2.5 rounded-lg border border-gray-200 bg-white text-[13px] text-gray-700"><span class="w-3 h-3 rounded-[3px]" style="background:${this._esc(t.color)}"></span>${this._esc(t.name)}<span class="text-gray-400">${t.existing_id ? this._t("import-existing") : this._t("import-new")}${t.sub ? " · sub" : ""}</span></span>`).join("")
    const options = (job) => {
      const seen = new Set()
      const list = []
      ;(job.candidates || []).forEach((c) => { if (!seen.has(c.id)) { seen.add(c.id); list.push(c) } })
      this.jobsValue.forEach((p) => { if (!seen.has(p.id)) { seen.add(p.id); list.push(p) } })
      return `<option value="">${this._esc(this._t("import-new-job"))}</option>` + list.map((p) => `<option value="${p.id}" ${Number(job.job_id) === p.id ? "selected" : ""}>${this._esc(p.name)}</option>`).join("")
    }
    this.importJobsTarget.innerHTML = (plan.jobs || []).map((job, i) => `<div data-import-row="${i}" class="flex items-center gap-3 px-3 py-2 text-[13px] ${job.skip ? "opacity-50" : ""}">
      <input type="checkbox" data-import-tick="${i}" data-action="change->crew-schedule#importTicked" ${job.skip ? "" : "checked"} class="form-checkbox rounded border-gray-300 text-purple-600 focus:ring-purple-500 shrink-0">
      <div class="flex-1 min-w-0"><div class="font-medium text-gray-800 truncate">${this._esc(job.name)}</div><div class="text-gray-400"><span data-import-now="${i}">${this._importNowText(job)}</span><span data-import-new="${i}">${job.job_id ? "" : ` · <span class="text-purple-700">${this._esc(this._t("import-new-job-note"))}</span>`}</span></div></div>
      <span class="text-gray-400">→</span>
      <select data-import-job="${i}" data-action="change->crew-schedule#importJobPicked" ${job.skip ? "disabled" : ""} class="w-72 px-2 py-1.5 text-[13px] border border-gray-300 rounded-lg bg-white text-gray-700">${options(job)}</select>
    </div>`).join("")
    this._importSummary()
  }

  // "12 now · 14 in the file" for a matched job; just the file's count otherwise.
  _importNowText(job) {
    const inFile = `${job.block_count} ${this._t("import-in-file")}`
    if (!job.job_id) return inFile
    const now = (this._importPlan && this._importPlan.existing_blocks && this._importPlan.existing_blocks[String(job.job_id)]) || 0
    return `${now} ${this._t("import-now")} · ${inFile}`
  }

  importJobPicked(e) {
    const i = Number(e.currentTarget.dataset.importJob), job = this._importPlan.jobs[i]
    job.job_id = e.currentTarget.value ? Number(e.currentTarget.value) : null
    const now = this.importJobsTarget.querySelector(`[data-import-now="${i}"]`); if (now) now.textContent = this._importNowText(job)
    const tag = this.importJobsTarget.querySelector(`[data-import-new="${i}"]`); if (tag) tag.innerHTML = job.job_id ? "" : ` · <span class="text-purple-700">${this._esc(this._t("import-new-job-note"))}</span>`
    this._importSummary()
  }

  // Untick a job to leave it out of the import.
  importTicked(e) {
    const i = Number(e.currentTarget.dataset.importTick), job = this._importPlan.jobs[i]
    job.skip = !e.currentTarget.checked
    const row = this.importJobsTarget.querySelector(`[data-import-row="${i}"]`); if (row) row.classList.toggle("opacity-50", job.skip)
    const sel = this.importJobsTarget.querySelector(`[data-import-job="${i}"]`); if (sel) sel.disabled = job.skip
    this._importSummary()
  }

  _importSummary() {
    const jobs = (this._importPlan.jobs || []).filter((j) => !j.skip)
    const blocks = jobs.reduce((n, j) => n + (j.blocks || []).length, 0)
    this.importApplyBtnTarget.textContent = `Import ${blocks} ${blocks === 1 ? "block" : "blocks"} on ${jobs.length} ${jobs.length === 1 ? "job" : "jobs"}`
    this.importApplyBtnTarget.disabled = blocks === 0
  }

  async importApply() {
    if (!this._importPlan) return
    this.importApplyBtnTarget.disabled = true
    const mode = this._importMode()
    ;(this._importPlan.jobs || []).forEach((j) => { j.action = "replace" })
    const res = await this._json(this.importApplyUrlValue, "POST", { plan: this._importPlan, mode })
    this.importApplyBtnTarget.disabled = false
    if (!res || !res.ok) return
    this.closeImport()
    const gone = (res.blocks_removed ? `, ${res.blocks_removed} ${this._t("import-cleared")}` : "") + (res.trades_removed ? `, ${res.trades_removed} ${this._t("import-trades-cleared")}` : "")
    this._toast(`${this._t("import-done")}: ${res.blocks_created} blocks, ${res.trades_created} new trades${gone}`)
    this._track("Crew Schedule Imported", { blocks: res.blocks_created, mode })
    // A clean slate after "replace everything": the board shows the file's jobs and nothing else.
    const ids = new Set(mode === "replace_all" ? [] : this.state.jobIds)
    ;(this._importPlan.jobs || []).forEach((j) => { if (j.job_id && !j.skip) ids.add(j.job_id) })
    if (res.new_job_ids) res.new_job_ids.forEach((id) => ids.add(id))
    this.state.jobIds = Array.from(ids)
    this.state.tab = "jobs"
    this._scrolledOnce = false
    await this.load()
  }

  // ── Manage trades ─────────────────────────────────────────────────

  openManage() { this._renderManage(); this.manageModalTarget.classList.remove("hidden") }
  closeManage() { if (this.hasManageModalTarget) this.manageModalTarget.classList.add("hidden") }

  _renderManage() {
    const inp = "px-2.5 py-1.5 text-[13px] border border-gray-300 rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand"
    const grid = this.manageListTarget.dataset.grid || "display:grid;grid-template-columns:20px 44px minmax(0,1fr) 96px 72px 64px 36px;gap:10px;align-items:center"
    this.manageListTarget.innerHTML = this.trades.map((t) => `<div style="${grid}" data-trade-row="${t.id}" draggable="true">
      <span data-trade-grip="1" title="Drag to reorder" class="text-gray-300 cursor-grab select-none text-[14px] leading-none text-center">⠿</span>
      <input type="color" value="${t.color}" data-field="color" data-trade-id="${t.id}" data-action="change->crew-schedule#saveTrade" title="Color" class="h-[30px] w-[44px] p-0.5 border border-gray-300 rounded-lg bg-white cursor-pointer">
      <input type="text" value="${this._esc(t.name)}" maxlength="60" data-field="name" data-trade-id="${t.id}" data-action="change->crew-schedule#saveTrade" class="${inp}">
      <input type="number" value="${t.crews}" min="1" max="99" data-field="crews_count" data-trade-id="${t.id}" data-action="change->crew-schedule#saveTrade" class="${inp}">
      <input type="number" value="${t.people ?? ""}" min="0" max="999" placeholder="—" data-field="people_per_crew" data-trade-id="${t.id}" data-action="change->crew-schedule#saveTrade" class="${inp}">
      <label class="flex items-center justify-center"><input type="checkbox" ${t.sub ? "checked" : ""} data-field="sub" data-trade-id="${t.id}" data-action="change->crew-schedule#saveTrade" class="form-checkbox rounded border-gray-300 text-brand"></label>
      <button type="button" data-trade-id="${t.id}" data-action="click->crew-schedule#deleteTrade" title="Remove" class="h-[30px] rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 cursor-pointer text-[15px]">×</button>
    </div>`).join("")
  }

  // Drag a trade row under Manage trades: this is the order job rows follow.
  tradeDragStart(e) {
    const row = e.target.closest("[data-trade-row]")
    if (!row || (e.target.closest("input") && e.target.tagName === "INPUT")) { if (!row) e.preventDefault(); return }
    this._tradeDrag = Number(row.dataset.tradeRow)
    this._tradeDragHeight = row.offsetHeight || 30
    const trade = this.trades.find((t) => t.id === this._tradeDrag)
    this._tradeDragName = trade ? trade.name : ""
    row.classList.add("cs-row-dragging")
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = "move"
      try { e.dataTransfer.setData("text/plain", String(this._tradeDrag)) } catch { /* Safari */ }
      try { const r = row.getBoundingClientRect(); e.dataTransfer.setDragImage(row, Math.max(0, e.clientX - r.left), Math.max(0, e.clientY - r.top)) } catch { /* jsdom */ }
    }
    setTimeout(() => { if (this._tradeDrag === Number(row.dataset.tradeRow)) row.classList.add("cs-row-collapsed") }, 0)
  }

  tradeDragOver(e) {
    if (!this._tradeDrag) return
    if (this._dropSlot && e.target.closest(".cs-drop-slot") === this._dropSlot) { e.preventDefault(); return }
    const row = e.target.closest("[data-trade-row]")
    if (!row) return
    e.preventDefault()
    if (e.dataTransfer) e.dataTransfer.dropEffect = "move"
    if (Number(row.dataset.tradeRow) === this._tradeDrag) { this._clearTradeSlot(); return }
    const r = row.getBoundingClientRect()
    this._placeSlot(row, e.clientY < r.top + r.height / 2, this._tradeDragHeight || 30, "0", this._tradeDragName)
  }

  _clearTradeSlot() { if (this._dropSlot && this._dropSlot.parentNode) this._dropSlot.parentNode.removeChild(this._dropSlot) }

  async tradeDrop(e) {
    const dragged = this._tradeDrag
    let row = e.target.closest("[data-trade-row]")
    let before = false, atEnd = false
    if (this._dropSlot && e.target.closest(".cs-drop-slot") === this._dropSlot) {
      let next = this._dropSlot.nextElementSibling
      while (next && next.classList.contains("cs-row-collapsed")) next = next.nextElementSibling
      if (next && next.dataset.tradeRow) { row = next; before = true } else { row = null; atEnd = true }
    } else if (row) {
      const r = row.getBoundingClientRect()
      before = e.clientY < r.top + r.height / 2
    }
    this.tradeDragEnd()
    if (!dragged || (!row && !atEnd)) return
    e.preventDefault()
    const targetId = row ? Number(row.dataset.tradeRow) : null
    if (targetId === dragged) return
    const order = this.trades.map((t) => t.id).filter((id) => id !== dragged)
    if (atEnd) order.push(dragged)
    else { const at = order.indexOf(targetId); order.splice(before ? at : at + 1, 0, dragged) }
    const byId = {}; this.trades.forEach((t) => { byId[t.id] = t })
    this.trades = order.map((id, i) => Object.assign(byId[id], { position: i }))
    this._renderManage(); this.render()
    const res = await this._json(this.tradesUrlValue, "PATCH", { trade_ids: order })
    if (res && res.ok && res.trades) { this.trades = res.trades; this._renderManage(); this.render() }
  }

  tradeDragEnd() {
    this._tradeDrag = null
    this._clearTradeSlot()
    if (this.hasManageListTarget) this.manageListTarget.querySelectorAll(".cs-drop-before, .cs-drop-after, .cs-row-dragging, .cs-row-collapsed").forEach((el) => el.classList.remove("cs-drop-before", "cs-drop-after", "cs-row-dragging", "cs-row-collapsed"))
  }

  async saveTrade(e) {
    const el = e.currentTarget, id = Number(el.dataset.tradeId)
    const value = el.dataset.field === "sub" ? (el.checked ? "1" : "0") : el.value
    const res = await this._json(`${this.tradesUrlValue}/${id}`, "PATCH", { trade: { [el.dataset.field]: value } })
    if (res && res.ok) { const i = this.trades.findIndex((t) => t.id === id); if (i > -1) this.trades[i] = res.trade; this._toast(this._t("saved")); this.render() }
  }

  async addTrade(e) {
    e.preventDefault()
    const form = e.currentTarget
    const data = { trade: { name: form.name.value.trim(), color: form.color.value, crews_count: form.crews_count.value, people_per_crew: form.people_per_crew ? form.people_per_crew.value : "", sub: form.sub.checked ? "1" : "0" } }
    if (!data.trade.name) return
    const res = await this._json(this.tradesUrlValue, "POST", data)
    if (res && res.ok) { this.trades.push(res.trade); form.reset(); form.color.value = "#0f9ed5"; this._renderManage(); this._track("Crew Trade Created", {}); this.render() }
  }

  async deleteTrade(e) {
    const id = Number(e.currentTarget.dataset.tradeId)
    const res = await this._json(`${this.tradesUrlValue}/${id}`, "DELETE")
    if (res && res.ok) { this.trades = this.trades.filter((t) => t.id !== id); if (this.state.tradeIds) this.state.tradeIds = this.state.tradeIds.filter((x) => x !== id); this._renderManage(); this.render() }
  }

  // ── Helpers ───────────────────────────────────────────────────────

  async _json(url, method, body) {
    try {
      const res = await fetch(url, { method, headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": this.csrfValue }, body: body ? JSON.stringify(body) : undefined })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) { this._toast(data.error || this._t("error"), "error"); return null }
      return data
    } catch { this._toast(this._t("error"), "error"); return null }
  }

  _styleTab(el, on) {
    el.classList.toggle("text-brand", on); el.classList.toggle("font-bold", on)
    el.classList.toggle("text-text-muted", !on); el.classList.toggle("font-medium", !on)
  }

  _parse(iso) { const [y, m, d] = String(iso).split("-").map(Number); return new Date(y, m - 1, d) }
  _iso(ms) { const d = new Date(ms); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}` }
  _day(iso) { return Math.round((this._parse(iso) - this.fromDate) / DAY_MS) }
  _dateAt(dayIdx) { return addDays(this.fromDate, dayIdx) }
  _fmt(dayIdx) { const d = this._dateAt(dayIdx); return `${MONTHS[d.getMonth()]} ${d.getDate()}` }
  _fmtEnd(e) { let d = e - 1; const dow = this._dateAt(d).getDay(); if (dow === 0) d -= 2; else if (dow === 6) d -= 1; return this._fmt(Math.max(d, 0)) }
  _todayIdx() { const t = new Date(); const idx = Math.round((new Date(t.getFullYear(), t.getMonth(), t.getDate()) - this.fromDate) / DAY_MS); return idx >= 0 && idx < this.range ? idx : null }
  _esc(s) { return String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])) }
  _t(key) { return this.element.dataset[`i18n${key.split("-").map((w) => w[0].toUpperCase() + w.slice(1)).join("")}`] || key }
  _svgCheck() { return '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M8 12.5l2.5 2.5L16 9.5"/></svg>' }
  _track(name, props) { try { if (typeof Mixpanel.track === "function") Mixpanel.track(name, props) } catch { /* analytics never breaks the page */ } }

  _toast(message, type = "success") {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const msg = document.createElement("p"); msg.className = "flex-1 text-sm"; msg.textContent = message
    el.appendChild(msg)
    document.body.prepend(el)
  }
}
